import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, Typography, Accordion, AccordionSummary, AccordionDetails, TextField, Checkbox, LinearProgress, CircularProgress, Dialog, DialogTitle, DialogContent, Alert, AlertTitle, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import axios from 'axios';
import { toast } from 'material-react-toastify';
import { PDFDocument } from 'pdf-lib';
import { APIURL } from 'src/config';
import PriorAuthTable, { resolveProcedureCode } from './PriorAuthTable';

const PDF_MIME = 'application/pdf';

const ACCEPTED_DOC_TYPES = new Set(['NJISP', 'SDR']);
const REJECTION_FALLBACK_MESSAGE = 'Only NJISP and Service Detail Report (SDR) documents are accepted.';

const MAX_FILE_SIZE_MB = 5;

/** Lowercase + strip non-alphanumerics so casing/punctuation/spacing don't cause false rejections. */
const normalizeCompanyName = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const SUPPORT_COORDINATION_BUSINESS_TYPE = 101;

const getPrimaryBusinessType = (user) => {
    const setting = Array.isArray(user?.comp_settings)
        ? user.comp_settings.find((s) => s?.feature_value === 'primaryBusinessType')
        : null;
    return setting?.feature_id ? Number(setting.feature_id) : null;
};

/** `customer_goal.goal_title` is VARCHAR(128). */
const GOAL_TITLE_MAX_LENGTH = 128;
const GOAL_TITLE_ELLIPSIS = '...';

/**
 * Length the API sees after it escapes the text for SQL (`'` -> `''`) and then measures it
 * against the column size. Budgeting against this -- rather than the raw length -- keeps the
 * server from having to truncate at all, which is what could otherwise split an escaped
 * quote in half.
 */
const escapedLength = (text) => text.length + (text.match(/'/g) || []).length;

/** Longest prefix of `text` whose escaped length still fits in `max`. */
const fitToEscapedLength = (text, max) => {
    let out = '';
    let cost = 0;
    for (const ch of text) {
        const chCost = ch === "'" ? 2 : ch.length;
        if (cost + chCost > max) break;
        out += ch;
        cost += chCost;
    }
    return out;
};

/**
 * Turn an outcome description into a `goal_title` that fits the column. Outcome descriptions
 * are full sentences and routinely run past 128 characters, so collapse whitespace and cut to
 * the limit -- back to a word boundary when one is near the end -- with an ellipsis marking
 * the cut. The untruncated text still goes to `goal_detail`.
 */
const toGoalTitle = (text) => {
    const clean = String(text || '').replace(/\s+/g, ' ').trim();
    if (escapedLength(clean) <= GOAL_TITLE_MAX_LENGTH) return clean;

    const budget = GOAL_TITLE_MAX_LENGTH - GOAL_TITLE_ELLIPSIS.length;
    const cut = fitToEscapedLength(clean, budget);
    const lastSpace = cut.lastIndexOf(' ');
    const body = lastSpace > cut.length - 24 ? cut.slice(0, lastSpace) : cut;
    return `${body.trimEnd()}${GOAL_TITLE_ELLIPSIS}`;
};

/** `permitted_task.goal_reference` is VARCHAR(255). */
const GOAL_REFERENCE_MAX_LENGTH = 255;

/**
 * `goal_reference` for a service authorization's permitted task: the free-text description
 * the plan prints under the service ("Kingsway day program"). Falls back to the printed
 * `Reference` code, which is what this field carried before descriptions were extracted, so
 * a service with no description does not end up blank. Newlines are collapsed because the
 * Permitted Task form renders this as a single-line field.
 */
const toGoalReference = (svc) => {
    const description = String(svc?.service_description || '').replace(/\s+/g, ' ').trim();
    const text = description || String(svc?.reference || '').replace(/\s+/g, ' ').trim();
    if (!text) return null;
    return fitToEscapedLength(text, GOAL_REFERENCE_MAX_LENGTH);
};

function scopeIspToCompany(details, normalizedCompany) {
    const all = Array.isArray(details?.service_authorizations) ? details.service_authorizations : [];
    const kept = all.filter((s) => normalizeCompanyName(s?.provider_name) === normalizedCompany);

    // Outcomes reachable from our own services. `associated_goals` carries the outcome each
    // service sits under, which is the same `outcome_number` `customer_goals` is keyed by.
    const keptOutcomes = new Set();
    for (const svc of kept) {
        const goals = Array.isArray(svc?.associated_goals) ? svc.associated_goals : [];
        for (const g of goals) {
            const n = String(g?.outcome_number ?? '').trim();
            if (n) keptOutcomes.add(n);
        }
    }

    const scoped = { ...details, service_authorizations: kept };

    if (Array.isArray(details?.customer_goals)) {
        scoped.customer_goals = details.customer_goals.filter((g) =>
            keptOutcomes.has(String(g?.outcome_number ?? '').trim())
        );
    }

    if (Array.isArray(details?.rate_change)) {
        // A rate change names the outcome it applies to. One with no outcome printed cannot be
        // attributed to any provider, so it is kept rather than silently losing a rate revision.
        scoped.rate_change = details.rate_change.filter((c) => {
            const n = String(c?.outcome_number ?? '').trim();
            return !n || keptOutcomes.has(n);
        });
    }

    return scoped;
}

/** Keys under `uploadDetails` to omit from accordions. */
const UPLOAD_DETAILS_SKIP_KEYS = new Set([
    'document_type',
    'plan_id',
    'documentType',
    'planId',
]);

/** Key rendered via PriorAuthTable instead of the generic accordion. */
const PRIOR_AUTH_KEY = 'prior_authorization_and_distribution';

/** Desired display order for upload-details accordions. Unknown keys are appended after. */
const ACCORDION_ORDER = [
    'customer_information',
    'diagnosis_information',
    'provider_information',
    'support_coordination_company',
    'guardianship_contacts',
    'preferred_hospital',
    'primary_care_physician',
    'administrative_service_organization',
    'managed_care_organization',
    'private_insurance',
    'employment_and_voting',
    'customer_goals',
    'customer_needs',
    'emergency_contacts',
    'medication_info',
    'medical_practitioners',
    'team_members_present',
    'service_authorizations',
    PRIOR_AUTH_KEY,
    'authorizations_and_signatures',
    'rate_change',
];

function formatLabel(key) {
    if (typeof key !== 'string') return String(key);
    return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getUploadDetailsKey(root) {
    if (!root || typeof root !== 'object' || Array.isArray(root)) return 'uploadDetails';
    if (Object.prototype.hasOwnProperty.call(root, 'uploadDetails')) return 'uploadDetails';
    if (Object.prototype.hasOwnProperty.call(root, 'upload_details')) return 'upload_details';
    return 'uploadDetails';
}

function getUploadDetails(root) {
    if (!root || typeof root !== 'object' || Array.isArray(root)) return null;
    return root.uploadDetails ?? root.upload_details ?? null;
}

function setInPath(obj, path, value) {
    if (path.length === 0) return value;
    if (obj === null || obj === undefined) return obj;
    const [head, ...rest] = path;
    if (rest.length === 0) {
        if (Array.isArray(obj)) {
            const copy = [...obj];
            copy[head] = value;
            return copy;
        }
        return { ...obj, [head]: value };
    }
    const child = obj[head];
    const newChild = setInPath(child, rest, value);
    if (Array.isArray(obj)) {
        const copy = [...obj];
        copy[head] = newChild;
        return copy;
    }
    return { ...obj, [head]: newChild };
}

/** Human-readable label: last property name as words (no path, no index labels). */
function pathToLabel(path) {
    if (!path || path.length === 0) return '';
    const last = path[path.length - 1];
    if (typeof last === 'string') return formatLabel(last);
    return '';
}

function formatDisplayValue(v) {
    if (v === null || v === undefined) return 'null';
    if (typeof v === 'boolean') return v ? 'true' : 'false';
    if (typeof v === 'object') {
        if (!hasData(v)) return '';
        return JSON.stringify(v);
    }
    return String(v);
}

/** True if the value has any non-empty leaf. Empty = null/undefined/""/[]/{} (recursive). */
function hasData(value) {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim() !== '';
    if (typeof value === 'number') return true;
    if (typeof value === 'boolean') return value;
    if (Array.isArray(value)) return value.some(hasData);
    if (typeof value === 'object') return Object.values(value).some(hasData);
    return false;
}

function SectionStatusChip({ filled }) {
    const label = filled ? 'Has data' : 'Empty';
    return (
        <Tooltip title={label} arrow placement="top">
            <Box
                component="span"
                aria-label={label}
                sx={{
                    ml: 1,
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    display: 'inline-block',
                    bgcolor: filled ? 'success.main' : 'error.main',
                    flexShrink: 0,
                }}
            />
        </Tooltip>
    );
}

/** Collect one row per leaf (primitive, null, or empty object/array). */
function flattenLeaves(value, basePath = []) {
    const rows = [];
    if (value === null || value === undefined) {
        rows.push({ path: basePath, value });
        return rows;
    }
    if (typeof value !== 'object') {
        rows.push({ path: basePath, value });
        return rows;
    }
    if (Array.isArray(value)) {
        if (value.length === 0) {
            rows.push({ path: basePath, value });
            return rows;
        }
        value.forEach((item, i) => {
            rows.push(...flattenLeaves(item, [...basePath, i]));
        });
        return rows;
    }
    const keys = Object.keys(value);
    if (keys.length === 0) {
        rows.push({ path: basePath, value });
        return rows;
    }
    keys.forEach((k) => {
        rows.push(...flattenLeaves(value[k], [...basePath, k]));
    });
    return rows;
}

function ObjectArrayCards({ items, scopeId, mode, onFieldChange, depth = 0 }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {items.map((item, idx) => (
                <Box
                    key={`${scopeId}-entry-${idx}`}
                    sx={{
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        p: 1.5,
                        bgcolor: 'background.default',
                    }}
                >
                    <Typography
                        variant="overline"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 1, lineHeight: 1.2 }}
                    >
                        Entry {idx + 1}
                    </Typography>
                    <KeyValueLeafRows
                        scopeId={`${scopeId}-entry-${idx}`}
                        rootValue={item}
                        mode={mode}
                        depth={depth}
                        onFieldChange={(subPath, newValue) =>
                            onFieldChange([idx, ...subPath], newValue)
                        }
                    />
                </Box>
            ))}
        </Box>
    );
}

function KeyValueLeafRows({ rootValue, onFieldChange, scopeId, mode = 'view', depth = 0 }) {
    const isObjArr = (v) =>
        Array.isArray(v)
        && v.length > 1
        && v.every((item) => item !== null && typeof item === 'object' && !Array.isArray(item));

    const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

    // Case 1: rootValue is directly an array of objects.
    if (isObjArr(rootValue)) {
        return (
            <ObjectArrayCards
                items={rootValue}
                scopeId={scopeId}
                mode={mode}
                depth={depth}
                onFieldChange={onFieldChange}
            />
        );
    }

    // Case 2: rootValue is an object with nested children (objects or arrays of objects).
    // Each nested child gets its own labelled group and recurses, so ancestor keys stay
    // visible — flattening them away would leave rows like "Subcategory Name" with no
    // indication of which category they belong to.
    if (isPlainObject(rootValue)) {
        const childKeys = Object.keys(rootValue);
        const groupChildKeys = childKeys.filter((k) => isObjArr(rootValue[k]) || isPlainObject(rootValue[k]));
        if (groupChildKeys.length > 0) {
            const flatChildKeys = childKeys.filter((k) => !groupChildKeys.includes(k));
            const flatSubset = flatChildKeys.reduce((acc, k) => ({ ...acc, [k]: rootValue[k] }), {});
            // Every key renders whether or not it holds data: this branch and the
            // flattenLeaves fallback below must agree, otherwise the same field appears
            // or vanishes depending on how many entries a sibling array happens to have.
            const hasFlatLeaves = flatChildKeys.length > 0;
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {hasFlatLeaves && (
                        <KeyValueLeafRows
                            scopeId={`${scopeId}-flat`}
                            rootValue={flatSubset}
                            mode={mode}
                            depth={depth}
                            onFieldChange={onFieldChange}
                        />
                    )}
                    {groupChildKeys.map((childKey) => (
                        <Box
                            key={`${scopeId}-sub-${childKey}`}
                            sx={depth > 0 ? {
                                pl: 1.5,
                                borderLeft: '2px solid',
                                borderColor: 'divider',
                            } : undefined}
                        >
                            <Typography
                                variant="subtitle2"
                                color={depth > 0 ? 'text.secondary' : 'text.primary'}
                                sx={{ mb: 1, fontWeight: 600 }}
                            >
                                {formatLabel(childKey)}
                            </Typography>
                            <KeyValueLeafRows
                                rootValue={rootValue[childKey]}
                                scopeId={`${scopeId}-sub-${childKey}`}
                                mode={mode}
                                depth={depth + 1}
                                onFieldChange={(path, newValue) =>
                                    onFieldChange([childKey, ...path], newValue)
                                }
                            />
                        </Box>
                    ))}
                </Box>
            );
        }
    }

    const rows = flattenLeaves(rootValue, []);
    const isEditing = mode === 'edit';

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {rows.map(({ path, value }) => {
                const id = `${scopeId}::${JSON.stringify(path)}`;
                const label = pathToLabel(path) || 'Value';
                const stringValue = value === null || value === undefined ? '' : String(value);
                const isLongString = typeof value === 'string' && value.length > 60;
                return (
                    <Box
                        key={id}
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { sm: 'flex-start' },
                            gap: 1,
                            py: 0.75,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                minWidth: { sm: 180 },
                                flexShrink: 0,
                                wordBreak: 'break-word',
                                mt: isEditing ? 1 : 0,
                            }}
                        >
                            {label}
                        </Typography>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            {isEditing ? (
                                typeof value === 'boolean' ? (
                                    <Checkbox
                                        checked={!!value}
                                        onChange={(e) => onFieldChange(path, e.target.checked)}
                                        size="small"
                                    />
                                ) : (
                                    <TextField
                                        size="small"
                                        fullWidth
                                        multiline={isLongString}
                                        minRows={isLongString ? 2 : 1}
                                        value={stringValue}
                                        onChange={(e) => onFieldChange(path, e.target.value)}
                                    />
                                )
                            ) : (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {formatDisplayValue(value)}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}

function SectionEditor({ value, scopeId, editable, onCommit }) {
    const [draft, setDraft] = useState(null);
    const isEditing = draft !== null;

    const startEdit = () => {
        setDraft(value === undefined ? null : JSON.parse(JSON.stringify(value ?? null)));
    };

    const cancelEdit = () => {
        setDraft(null);
    };

    const saveEdit = () => {
        onCommit(draft);
        setDraft(null);
    };

    const updateDraft = (path, newValue) => {
        setDraft((prev) => setInPath(prev, path, newValue));
    };

    return (
        <Box>
            {editable && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1.5 }}>
                    {!isEditing ? (
                        <Button size="small" variant="outlined" onClick={startEdit}>
                            Edit
                        </Button>
                    ) : (
                        <>
                            <Button size="small" variant="contained" onClick={saveEdit}>
                                Save
                            </Button>
                            <Button size="small" onClick={cancelEdit}>
                                Cancel
                            </Button>
                        </>
                    )}
                </Box>
            )}
            <KeyValueLeafRows
                scopeId={scopeId}
                rootValue={isEditing ? draft : value}
                mode={isEditing ? 'edit' : 'view'}
                onFieldChange={isEditing ? updateDraft : () => {}}
            />
        </Box>
    );
}

function SdrUploadDetailsAccordions({
    uploadDetails,
    expandedKey,
    onExpandedKeyChange,
    onSectionFieldChange,
    onArrayItemFieldChange,
    onPrimitiveChange,
}) {
    if (uploadDetails === null || uploadDetails === undefined) {
        return (
            <Typography variant="body2" color="text.secondary">
                No upload details in the response.
            </Typography>
        );
    }

    if (Array.isArray(uploadDetails)) {
        if (uploadDetails.length === 0) {
            return (
                <Typography variant="body2" color="text.secondary">
                    Upload details are empty.
                </Typography>
            );
        }
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5, flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {uploadDetails.map((item, index) => {
                    const panelKey = `__idx_${index}`;
                    return (
                        <Accordion
                            key={panelKey}
                            disableGutters
                            variant="outlined"
                            sx={{ '&:before': { display: 'none' } }}
                            expanded={expandedKey === panelKey}
                            onChange={(_, isExpanded) =>
                                onExpandedKeyChange(isExpanded ? panelKey : false)
                            }
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center' } }}
                            >
                                <Typography variant="subtitle1" fontWeight={600}>
                                    Item {index + 1}
                                </Typography>
                                <SectionStatusChip filled={hasData(item)} />
                            </AccordionSummary>
                            <AccordionDetails>
                                <KeyValueLeafRows
                                    scopeId={`arr-${index}`}
                                    rootValue={item}
                                    onFieldChange={(path, newValue) =>
                                        onArrayItemFieldChange(index, path, newValue)
                                    }
                                />
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Box>
        );
    }

    if (typeof uploadDetails === 'boolean') {
        return (
            <KeyValueLeafRows
                scopeId="primitive-bool"
                rootValue={uploadDetails}
                onFieldChange={(path, newValue) => {
                    if (path.length === 0) onPrimitiveChange(newValue);
                }}
            />
        );
    }

    if (typeof uploadDetails !== 'object' || uploadDetails === null) {
        const str =
            typeof uploadDetails === 'string' || typeof uploadDetails === 'number'
                ? String(uploadDetails)
                : '';
        return (
            <KeyValueLeafRows
                scopeId="primitive"
                rootValue={uploadDetails === null ? null : uploadDetails}
                onFieldChange={(path, newValue) => {
                    if (path.length === 0) onPrimitiveChange(newValue);
                }}
            />
        );
    }

    const presentKeys = Object.keys(uploadDetails).filter((k) => {
        if (UPLOAD_DETAILS_SKIP_KEYS.has(k)) return false;
        if (k === PRIOR_AUTH_KEY) {
            const v = uploadDetails[k];
            return v !== undefined && v !== null;
        }
        return true;
    });
    const orderIndex = new Map(ACCORDION_ORDER.map((k, i) => [k, i]));
    const orderedKeys = [...presentKeys].sort((a, b) => {
        const ai = orderIndex.has(a) ? orderIndex.get(a) : ACCORDION_ORDER.length;
        const bi = orderIndex.has(b) ? orderIndex.get(b) : ACCORDION_ORDER.length;
        if (ai !== bi) return ai - bi;
        return presentKeys.indexOf(a) - presentKeys.indexOf(b);
    });

    const handlePriorAuthSave = (newData) => {
        onSectionFieldChange(PRIOR_AUTH_KEY, [], newData);
    };

    if (orderedKeys.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                Upload details are empty.
            </Typography>
        );
    }

    const sectionDomId = (key) => `sdr-section-${key}`;
    const handleNavClick = (key) => (e) => {
        e.preventDefault();
        const el = document.getElementById(sectionDomId(key));
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <Box sx={{ display: 'flex', gap: 3, mt: 0.5, alignItems: 'stretch', flex: 1, minHeight: 0, height: '100%' }}>
            <Box
                component="nav"
                aria-label="Section navigation"
                sx={{
                    display: { xs: 'none', md: 'block' },
                    width: 240,
                    flexShrink: 0,
                    overflowY: 'auto',
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    pr: 1,
                }}
            >
                {orderedKeys.map((key) => (
                    <Box
                        key={`nav-${key}`}
                        component="a"
                        href={`#${sectionDomId(key)}`}
                        onClick={handleNavClick(key)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            py: 0.75,
                            px: 1,
                            textDecoration: 'none',
                            color: 'text.primary',
                            borderRadius: 1,
                            '&:hover': { bgcolor: 'action.hover' },
                        }}
                    >
                        <SectionStatusChip filled={hasData(uploadDetails[key])} />
                        <Typography variant="body2" sx={{ ml: 0.5 }}>
                            {formatLabel(key)}
                        </Typography>
                    </Box>
                ))}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 3, pr: 1 }}>
                {orderedKeys.map((key) => {
                    const isPriorAuth = key === PRIOR_AUTH_KEY;
                    const value = uploadDetails[key];
                    return (
                        <Box
                            key={key}
                            id={sectionDomId(key)}
                            sx={{
                                scrollMarginTop: 16,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                p: 2,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {formatLabel(key)}
                                </Typography>
                                <SectionStatusChip filled={hasData(value)} />
                            </Box>
                            {isPriorAuth ? (
                                <PriorAuthTable
                                    data={value}
                                    serviceAuthorizations={uploadDetails.service_authorizations}
                                    rateChanges={uploadDetails.rate_change}
                                    customerInformation={uploadDetails.customer_information}
                                    onSave={handlePriorAuthSave}
                                />
                            ) : (
                                <SectionEditor
                                    scopeId={`sec-${key}`}
                                    value={value}
                                    editable
                                    onCommit={(newSection) => onSectionFieldChange(key, [], newSection)}
                                />
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

// Sequential stages keyed to seconds elapsed. The last stage reached wins;
// 'Almost done...' kicks in once we cross 85% of the estimate (or as the
// final fallback after 90s when no estimate is available).
const PROCESSING_STAGES = [
    { startAt: 0, label: 'Uploading PDF...' },
    { startAt: 4, label: 'Processing document...' },
    { startAt: 12, label: 'Extracting document entities...' },
    { startAt: 30, label: 'Synthesizing structured data...' },
];
const ALMOST_DONE_LABEL = 'Almost done...';
const ALMOST_DONE_THRESHOLD_RATIO = 0.85;
const ALMOST_DONE_FALLBACK_SECONDS = 90;

const pickProcessingMessage = (elapsed, estimated) => {
    const lastStageStart = PROCESSING_STAGES[PROCESSING_STAGES.length - 1].startAt;
    const almostDoneAt = estimated
        ? Math.max(lastStageStart + 5, estimated * ALMOST_DONE_THRESHOLD_RATIO)
        : ALMOST_DONE_FALLBACK_SECONDS;
    if (elapsed >= almostDoneAt) return ALMOST_DONE_LABEL;
    let label = PROCESSING_STAGES[0].label;
    for (const stage of PROCESSING_STAGES) {
        if (elapsed >= stage.startAt) label = stage.label;
        else break;
    }
    return label;
};

// Heuristic: backend overhead + PDF ingestion + LLM generation at ~50 tok/s
// with a schema-bounded output of ~2000 baseline tokens + ~100 per page.
const estimateProcessingSeconds = (pageCount) => {
    const pages = Number.isFinite(pageCount) && pageCount > 0 ? pageCount : 0;
    return Math.round(45 + pages * 2.8);
};

const formatDuration = (totalSeconds) => {
    const s = Math.max(0, Math.round(totalSeconds));
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return rem === 0 ? `${m}m` : `${m}m ${rem}s`;
};

const countPdfPages = async (file) => {
    try {
        const buffer = await file.arrayBuffer();
        const pdf = await PDFDocument.load(buffer, { ignoreEncryption: true, throwOnInvalidObject: false });
        return pdf.getPageCount();
    } catch {
        return null;
    }
};

const EXECUTION_STEPS = [
    'customer',
    'contacts',
    'preferred_hospital',
    'payers',
    'service_authorizations',
    'project_lookup',
    'customer_goals',
    'health_needs',
    'support_needs',
    'employment_needs',
    'medications',
    'task_types',
    'permitted_tasks',
];

const parseMedicationSearchTerm = (rawName) => {
    const trimmed = String(rawName || '').trim();
    if (!trimmed) return '';
    return trimmed.replace(/\s*\([^)]*\)\s*$/, '').trim();
};

const toNormalizedDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
};

const splitFullName = (fullName) => {
    const raw = String(fullName || '').trim();
    if (!raw) return { firstName: '', lastName: '' };
    const parts = raw.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return { firstName: parts[0], lastName: '' };
    return { firstName: parts.slice(0, -1).join(' '), lastName: parts[parts.length - 1] };
};

const parseStoredAddressString = (raw) => {
    const str = String(raw || '').trim();
    if (!str) return [];
    return str.split(':').map((entry) => {
        const parts = entry.split(',').map((p) => p.trim());
        return {
            address_line_1: parts[0] || '',
            address_line_2: parts[1] || '',
            city: parts[2] || '',
            state: parts[3] || '',
            zip: parts[4] || '',
            latitude: 0,
            longitude: 0,
        };
    });
};

const mapExistingCustomerToPayload = (existing) => {
    if (!existing) return {};
    const addr = parseStoredAddressString(existing.cus_address);
    return {
        cus_name: existing.cus_name || '',
        name: existing.cus_name || '',
        first_name: existing.cus_first_name || '',
        last_name: existing.cus_last_name || '',
        gender: existing.gender || '',
        dob: existing.dob || null,
        email: existing.cus_email || '',
        cus_local_id: existing.cus_local_id || '',
        phonList: existing.cus_phone || '',
        phone: existing.cus_phone || '',
        acquired_date: existing.cus_acquired_date || null,
        status: existing.cus_status || 'Active',
        type: existing.cus_type || 'Individual',
        customer_type: existing.cus_type || 'Individual',
        is_company: existing.cus_is_company ? 'true' : 'false',
        medicaid_id: existing.medicaid_id || '',
        medicaid_eligible: existing.medicaid_eligible !== undefined ? existing.medicaid_eligible : null,
        sign_page_date: existing.sign_page_date || null,
        last_plan_approval_date: existing.last_plan_approval_date || null,
        last_assess_date: existing.last_assess_date || null,
        renewal_date: existing.renewal_date || null,
        DDDID: existing.DDDID || '',
        ddd_id: existing.DDDID || '',
        DDD_tier: existing.DDD_tier || '',
        diagnose_code: existing.PrimaryDiagnosisCode || '',
        secondary_diagnose_code: existing.SecondaryDiagnosisCode || '',
        func_criteria_met: existing.func_criteria_met !== undefined ? existing.func_criteria_met : null,
        waiver_program: existing.waiver_program || '',
        waiver_prgm: existing.waiver_program || '',
        age: existing.cus_age || '',
        NJCATSelfcare: existing.NJCATSelfcare !== undefined ? existing.NJCATSelfcare : null,
        NJCATBehavioral: existing.NJCATBehavioral !== undefined ? existing.NJCATBehavioral : null,
        NJCATMedical: existing.NJCATMedical !== undefined ? existing.NJCATMedical : null,
        NJCATFCA: existing.NJCATFCA || '',
        MedicaidEndDate: existing.MedicaidEndDate || null,
        AgencyAssignDate: existing.AgencyAssignDate || null,
        County: existing.County || '',
        Race: existing.Race || '',
        Ethnicity: existing.Ethnicity || '',
        TransferOutReason: existing.TransferOutReason || '',
        TransferOutDate: existing.TransferOutDate || null,
        mailing_detail: existing.mailing_detail || '',
        place01char: existing.place01char || '',
        place02char: existing.place02char || '',
        place03char: existing.place03char || '',
        place04char: existing.place04char || '',
        place05char: existing.place05char || '',
        place06char: existing.place06char || '',
        place07char: existing.place07char || '',
        place08char: existing.place08char || '',
        place09date: existing.place09date || null,
        place10date: existing.place10date || null,
        place11date: existing.place11date || null,
        place12date: existing.place12date || null,
        place13number: existing.place13number !== undefined ? existing.place13number : null,
        place14number: existing.place14number !== undefined ? existing.place14number : null,
        place15number: existing.place15number !== undefined ? existing.place15number : null,
        place16number: existing.place16number !== undefined ? existing.place16number : null,
        HasPet: existing.HasPet !== undefined ? existing.HasPet : null,
        ProviderGenderPreference: existing.ProviderGenderPreference || '',
        LanguagePreference: existing.LanguagePreference || '',
        PreferRepeatProviders: existing.PreferRepeatProviders !== undefined ? existing.PreferRepeatProviders : null,
        DriverLicenseNumber: existing.DriverLicenseNumber || '',
        DLExpiryDate: existing.DLExpiryDate || null,
        DLIssueDate: existing.DLIssueDate || null,
        DLIssueState: existing.DLIssueState || '',
        address: addr,
        addList: addr,
    };
};

const mergeSdrOverExisting = (existing, sdr) => {
    const merged = { ...existing };
    for (const [key, val] of Object.entries(sdr)) {
        if (val === '' || val === null || val === undefined) continue;
        if (Array.isArray(val)) {
            if (val.length === 0) continue;
            if ((key === 'address' || key === 'addList') && !val.some((a) => a?.address_line_1 || a?.city || a?.state || a?.zip)) continue;
        }
        merged[key] = val;
    }
    return merged;
};

/**
 * `project.curr_plan_version` is a FLOAT, while the document prints the plan version as text
 * ("3.00"). Returns null for anything non-numeric so a stray value is not stored as 0 --
 * Number('') and Number(null) are both 0, so those are screened out before parsing.
 */
const toPlanVersion = (value) => {
    if (value === null || value === undefined || String(value).trim() === '') return null;
    const n = Number(String(value).trim());
    return Number.isFinite(n) ? n : null;
};

/**
 * Major (whole-number) part of a plan version: "3.00" -> 3, 2.5 -> 2. Null when the value is
 * missing or not numeric. A change here means a new plan; a change only after the decimal is
 * a revision of the same plan.
 */
const toPlanMajor = (value) => {
    const n = toPlanVersion(value);
    return n === null ? null : Math.trunc(n);
};

/** The customer's active project this document belongs to, preferring a DDD ID match. */
const matchExistingProject = (projects, customerId, companyId, dddId) => {
    if (!Array.isArray(projects)) return null;
    const isActive = (p) => p.pro_is_active === true || p.pro_is_active === 1 || String(p.pro_is_active).toLowerCase() === 'true';
    const customerActiveProjects = projects.filter(p =>
        String(p.pro_customer_id) === String(customerId) &&
        String(p.pro_com_id) === String(companyId) &&
        isActive(p)
    );
    return (
        customerActiveProjects.find(p => dddId && String(p.DDD_ID) === String(dddId)) ||
        customerActiveProjects.sort((a, b) => Number(b.pro_id) - Number(a.pro_id))[0] ||
        null
    );
};

const mapExistingProjectToPayload = (existing) => {
    if (!existing) return {};
    const pLoc = Array.isArray(existing.locations)
        ? existing.locations.map((l) => ({ locId: l.loc_id }))
        : [];
    return {
        projId: existing.pro_id,
        pTitle: existing.pro_title || '',
        ddd_id: existing.DDD_ID || '',
        active_plan_version: existing.active_plan_version != null ? String(existing.active_plan_version) : '',
        curr_plan_version: toPlanVersion(existing.curr_plan_version),
        pDesc: existing.pro_description || '',
        pEndDate: existing.pro_ends_on || null,
        pstrtDate: existing.pro_starts_on || null,
        pPrice: existing.pro_price != null ? existing.pro_price : 0,
        pCurrency: existing.pro_currency || 'USD',
        pSponsor: existing.pro_sponsors_id != null ? existing.pro_sponsors_id : null,
        pManager: existing.pro_manager_id != null ? existing.pro_manager_id : null,
        pContact: existing.pro_contact_id != null ? existing.pro_contact_id : null,
        pCustomer: existing.pro_customer_id,
        company_id: existing.pro_com_id,
        LastAnnualVisitDate: existing.LastAnnualVisitDate || null,
        pro_self_pay: existing.pro_self_pay || '',
        pro_primary_payer: existing.pro_primary_payer != null ? existing.pro_primary_payer : null,
        pro_secondary_payer: existing.pro_secondary_payer != null ? existing.pro_secondary_payer : null,
        place01char: existing.place01char || '',
        place02char: existing.place02char || '',
        place03char: existing.place03char || '',
        place04date: existing.place04date || null,
        place05date: existing.place05date || null,
        place06number: existing.place06number != null ? existing.place06number : null,
        place07number: existing.place07number != null ? existing.place07number : null,
        user_id: existing.pro_updated_by != null ? existing.pro_updated_by : null,
        pLoc,
        selectedEmp: [],
    };
};

const US_STATE_CODES = {
    'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR', 'california': 'CA', 'colorado': 'CO',
    'connecticut': 'CT', 'delaware': 'DE', 'district of columbia': 'DC', 'florida': 'FL', 'georgia': 'GA',
    'hawaii': 'HI', 'idaho': 'ID', 'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA', 'kansas': 'KS',
    'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD', 'massachusetts': 'MA',
    'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS', 'missouri': 'MO', 'montana': 'MT',
    'nebraska': 'NE', 'nevada': 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM',
    'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH', 'oklahoma': 'OK',
    'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC', 'south dakota': 'SD',
    'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT', 'vermont': 'VT', 'virginia': 'VA', 'washington': 'WA',
    'west virginia': 'WV', 'wisconsin': 'WI', 'wyoming': 'WY'
};

// Returns the two letter code for a state written either way ("NJ" / "New Jersey"), or '' when the
// value is not a state at all.
const toStateCode = (value) => {
    const raw = String(value || '').trim();
    if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
    return US_STATE_CODES[raw.toLowerCase()] || '';
};

// The customer record stores gender as M/F/Other; SDR documents spell it out ("Female").
const normalizeGenderCode = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const first = raw[0].toUpperCase();
    if (first === 'M') return 'M';
    if (first === 'F') return 'F';
    return 'Other';
};

const parseAddress = (address) => {
    const raw = String(address || '').trim();
    const empty = { addressLine1: '', addressLine2: '', city: '', state: '', zip: '' };
    if (!raw) return empty;
    const parts = raw.replace(/\n/g, ', ').split(',').map((part) => part.trim()).filter(Boolean);
    if (parts.length === 0) return empty;
    if (parts.length === 1) return { ...empty, addressLine1: parts[0] };

    let addressLine1 = '';
    let addressLine2 = '';
    let city = '';
    let state = '';
    let zip = '';

    const work = [...parts];
    const lastTokens = String(work[work.length - 1]).split(/\s+/).filter(Boolean);

    if (lastTokens.length > 0 && /^\d{5}(-\d{4})?$/.test(lastTokens[lastTokens.length - 1])) {
        zip = lastTokens.pop();
    }
    // The state trails the zip either as a code ("NJ 07307") or as a full name ("New Jersey 07307"),
    // and may be the last token outright when the address carries no zip.
    if (lastTokens.length > 0) {
        const twoWordState = lastTokens.length > 1 ? toStateCode(lastTokens.slice(-2).join(' ')) : '';
        const oneWordState = toStateCode(lastTokens[lastTokens.length - 1]);
        if (twoWordState) {
            state = twoWordState;
            lastTokens.splice(-2, 2);
        } else if (oneWordState) {
            state = oneWordState;
            lastTokens.pop();
        }
    }

    if (lastTokens.length > 0) {
        city = lastTokens.join(' ');
        work.pop();
    } else {
        work.pop();
        if (work.length > 0) {
            // The state can also be its own comma separated segment ("..., Jersey City, New Jersey, 07307").
            if (!state) {
                const prevState = toStateCode(work[work.length - 1]);
                if (prevState) {
                    state = prevState;
                    work.pop();
                }
            }
            if (work.length > 0) {
                city = work.pop();
            }
        }
    }

    if (work.length > 0) addressLine1 = work[0];
    if (work.length > 1) addressLine2 = work.slice(1).join(', ');

    return { addressLine1, addressLine2, city, state, zip };
};

const parseProcedureCode = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return { procedureCode: '', procedureCodeModifier: '' };
    if (raw.includes(':')) {
        const split = raw.split(':');
        return { procedureCode: split[0] || '', procedureCodeModifier: split[1] || '' };
    }
    if (raw.length > 5) {
        return { procedureCode: raw.slice(0, 5), procedureCodeModifier: raw.slice(5) };
    }
    return { procedureCode: raw, procedureCodeModifier: '' };
};

const getIdFromPayload = (payload) => {
    if (payload === null || payload === undefined) return null;
    if (typeof payload === 'number' || typeof payload === 'string') return payload;
    if (Array.isArray(payload)) {
        const first = payload[0] || {};
        return first.id || first.cus_id || first.payer_id || first.primaryID || first.customer_goal_id || null;
    }
    return payload.id || payload.cus_id || payload.payer_id || payload.primaryID || payload?.payer_data?.payer_id || null;
};

const isApiFailure = (data) => {
    if (!data || typeof data !== 'object') return false;
    const code = Number(data.code);
    return Number.isFinite(code) && code >= 400;
};

const apiFailureReason = (data, fallback) =>
    (data && typeof data === 'object' && (data.message || data.error)) || fallback || 'Server reported failure';

/**
 * Fixed `customer_needs` categories, mirroring the `customer_health_need` /
 * `customer_support_need` columns the execute flow writes to.
 */
const NEED_GROUPS = {
    health_needs: ['allergies', 'dietary', 'health_hazards', 'self_care'],
    support_needs: ['behavior', 'mobility', 'religious_cultural', 'support_settings'],
};

/** Legacy keyword matching for payloads extracted before the fixed categories existed. */
const LEGACY_NEED_GROUP_KEYS = {
    health_needs: 'health_and_nutrition_needs',
    support_needs: 'safety_and_support_needs',
};
const LEGACY_NEED_KEYWORDS = {
    allergies: ['allerg'],
    dietary: ['diet', 'nutrition'],
    health_hazards: ['hazard', 'medical', 'health'],
    self_care: ['self', 'care'],
    behavior: ['behav', 'commun'],
    mobility: ['mobil'],
    religious_cultural: ['religio', 'cultur'],
    support_settings: ['support', 'setting'],
};

/** Flatten one need category ({ summary, subcategories }) into a single stored string. */
const getNeedText = (category) => {
    if (!category) return '';
    const parts = [];
    // `category_description` is the legacy field name for `summary`.
    const summary = String(category?.summary ?? category?.category_description ?? '').trim();
    if (summary) parts.push(summary);
    if (Array.isArray(category?.subcategories)) {
        category.subcategories.forEach((sub) => {
            const description = String(sub?.need_description || '').trim();
            if (!description) return;
            const name = String(sub?.subcategory_name || '').trim();
            parts.push(name ? `${name}: ${description}` : description);
        });
    }
    return parts.join(' | ');
};

/**
 * Read one fixed category out of `customer_needs`, falling back to keyword-matching the
 * legacy free-form arrays so previously stored payloads still execute.
 */
const resolveNeedText = (customerNeeds, group, key) => {
    const category = customerNeeds?.[group]?.[key];
    if (category) return getNeedText(category);

    const legacyNeeds = customerNeeds?.[LEGACY_NEED_GROUP_KEYS[group]];
    if (!Array.isArray(legacyNeeds)) return '';
    const keywords = LEGACY_NEED_KEYWORDS[key] || [];
    return legacyNeeds
        .filter((item) => keywords.some((kw) => String(item?.category_name || '').toLowerCase().includes(kw)))
        .map(getNeedText)
        .filter(Boolean)
        .join(' | ');
};

const SDRUploadForm = ({ showUploader = true, initialResult = null, companyId = null, comp_id = null, user = null }) => {
    const [file, setFile] = useState(null);
    const [error, setError] = useState('');
    const [sdrResult, setSdrResult] = useState(initialResult);
    const [expandedAccordionKey, setExpandedAccordionKey] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [pageCount, setPageCount] = useState(null);
    const [estimatedSeconds, setEstimatedSeconds] = useState(null);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [uploadedFilename, setUploadedFilename] = useState(null);
    const [documentUrl, setDocumentUrl] = useState(null);
    const [recentUploads, setRecentUploads] = useState([]);
    const [showExecutionDialog, setShowExecutionDialog] = useState(false);
    const [isExecuting, setIsExecuting] = useState(false);
    const [currentExecuteStep, setCurrentExecuteStep] = useState(0);
    const [executionResults, setExecutionResults] = useState([]);
    const [viewingResults, setViewingResults] = useState(false);
    const [viewingMeta, setViewingMeta] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const inputRef = useRef(null);
    const resolvedCompanyId = companyId ?? comp_id ?? null;
    const isSupportCoordinationCompany = getPrimaryBusinessType(user) === SUPPORT_COORDINATION_BUSINESS_TYPE;

    useEffect(() => {
        if (!isProcessing) return undefined;
        setElapsedSeconds(0);
        const startedAt = Date.now();
        const tick = setInterval(() => {
            setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
        }, 1000);
        return () => clearInterval(tick);
    }, [isProcessing]);

    useEffect(() => {
        setExpandedAccordionKey(false);
    }, [sdrResult]);

    useEffect(() => {
        if (initialResult !== null && initialResult !== undefined) {
            setSdrResult(initialResult);
        }
    }, [initialResult]);

    // Revoke the object URL of the previously-uploaded document to avoid memory leaks.
    useEffect(() => {
        if (!documentUrl) return undefined;
        return () => URL.revokeObjectURL(documentUrl);
    }, [documentUrl]);

    const fetchRecentUploads = async () => {
        if (!resolvedCompanyId) return;
        try {
            const resp = await axios.get(`${APIURL}/sdr/uploads`, {
                params: { com_id: resolvedCompanyId, limit: 5 },
            });
            setRecentUploads(Array.isArray(resp.data) ? resp.data : []);
        } catch (err) {
            console.error('Failed to load recent uploads', err);
        }
    };

    useEffect(() => {
        fetchRecentUploads();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvedCompanyId]);

    const updateUploadDetailsRoot = (updater) => {
        setSdrResult((prev) => {
            if (!prev) return prev;
            const udKey = getUploadDetailsKey(prev);
            const ud = prev[udKey];
            const nextUd = updater(ud);
            return { ...prev, [udKey]: nextUd };
        });
    };

    const onSectionFieldChange = (sectionKey, path, newValue) => {
        updateUploadDetailsRoot((ud) => {
            if (!ud || typeof ud !== 'object' || Array.isArray(ud)) return ud;
            const section = ud[sectionKey];
            const newSection = setInPath(section, path, newValue);
            return { ...ud, [sectionKey]: newSection };
        });
    };

    const onArrayItemFieldChange = (index, path, newValue) => {
        updateUploadDetailsRoot((ud) => {
            if (!Array.isArray(ud)) return ud;
            const section = ud[index];
            const newSection = setInPath(section, path, newValue);
            const copy = [...ud];
            copy[index] = newSection;
            return copy;
        });
    };

    const onPrimitiveChange = (next) => {
        updateUploadDetailsRoot(() => next);
    };

    const selectFile = (fileList) => {
        setError('');

        if (!fileList || fileList.length === 0) {
            return;
        }

        if (fileList.length > 1) {
            setError('Only 1 document can be uploaded at a time.');
            return;
        }

        const selectedFile = fileList[0];

        if (selectedFile.type !== PDF_MIME) {
            setError('Please select a PDF file.');
            setFile(null);
            return;
        }

        if (selectedFile.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
            setError(`File is too large. Maximum size is ${MAX_FILE_SIZE_MB} MB.`);
            setFile(null);
            return;
        }

        setFile(selectedFile);
        setPageCount(null);
        setEstimatedSeconds(null);
        countPdfPages(selectedFile).then((pages) => {
            if (!pages) return;
            setPageCount(pages);
            setEstimatedSeconds(estimateProcessingSeconds(pages));
        });
    };

    const handleFileChange = (e) => {
        selectFile(e.target.files);
        e.target.value = '';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isDragging) setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        selectFile(e.dataTransfer?.files);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file) {
            setError('Please select a PDF file first.');
            return;
        }
        setError('');
        setIsProcessing(true);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await axios.post(`${APIURL}/sdr/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            let payload = response.data;
            if (typeof payload === 'string') {
                try {
                    payload = JSON.parse(payload);
                } catch {
                    toast.error('Unexpected response format from server.');
                    return;
                }
            }
            console.log('LLM Return JSON:', JSON.stringify(payload, null, 2));

            const detectedType = String(payload?.uploadDetails?.document_type || '').toUpperCase().trim();
            const isValidDocument = payload?.isValidDocument !== false && ACCEPTED_DOC_TYPES.has(detectedType);

            if (!isValidDocument) {
                toast.error(payload?.rejectionReason || REJECTION_FALLBACK_MESSAGE);
                setSdrResult(null);
                handleClear();
                return;
            }

            if (isSupportCoordinationCompany && detectedType === 'SDR') {
                toast.error('Service Detail Reports (SDR) are not accepted for Support Coordination companies. Please upload an NJ Individualized Service Plan (NJISP) instead.');
                setSdrResult(null);
                handleClear();
                return;
            }

            // Ownership check: the company printed on the document must be the logged-in
            // company. Where it is printed depends on who is uploading what:
            //   - ISP uploaded by a Support Coordination company: that company is the
            //     coordinating agency, named in the "Support Coordination" block.
            //   - ISP uploaded by anyone else: the plan names a provider per service
            //     authorization, so the company must own at least one of them.
            //   - SDR (non-SC companies only): the provider name under "Provider Information".
            const details = getUploadDetails(payload) || {};
            const userCompany = normalizeCompanyName(user?.company_name);
            const isIsp = detectedType === 'NJISP';
            const matchOnSupportCoordination = isIsp && isSupportCoordinationCompany;
            const documentCompanies = matchOnSupportCoordination
                ? [normalizeCompanyName(details.support_coordination_company?.company_name)].filter(Boolean)
                : isIsp
                    ? (Array.isArray(details.service_authorizations) ? details.service_authorizations : [])
                        .map((s) => normalizeCompanyName(s?.provider_name))
                        .filter(Boolean)
                    : [normalizeCompanyName(details.provider_information?.provider_name)].filter(Boolean);

            if (!userCompany) {
                toast.error('Your account has no company name set, so document ownership cannot be verified. Please contact support.');
                setSdrResult(null);
                handleClear();
                return;
            }

            if (documentCompanies.length === 0) {
                toast.error(
                    matchOnSupportCoordination
                        ? `Could not read the support coordination company name from this document, so it could not be matched to your company ("${user.company_name}").`
                        : isIsp
                            ? `Could not read any service authorization provider name from this document, so it could not be matched to your company ("${user.company_name}").`
                            : `Could not read the provider company name from this document, so it could not be matched to your company ("${user.company_name}").`
                );
                setSdrResult(null);
                handleClear();
                return;
            }

            if (!documentCompanies.includes(userCompany)) {
                toast.error(`This document belongs to a different company. It must match your logged-in company${user?.company_name ? ` ("${user.company_name}")` : ''}.`);
                setSdrResult(null);
                handleClear();
                return;
            }

            // The ISP matched on at least one service authorization, but the rest of the plan
            // may belong to other agencies. Narrow it to our own services -- and to the
            // outcomes only they reach -- before anything displays, ingests, or persists it.
            // A Support Coordination company matched on the coordinating agency instead: it
            // coordinates every service on the plan, so nothing is scoped away for them.
            const acceptedPayload = isIsp && !matchOnSupportCoordination
                ? { ...payload, [getUploadDetailsKey(payload)]: scopeIspToCompany(details, userCompany) }
                : payload;

            setSdrResult(acceptedPayload);
            setUploadedFilename(file?.name || null);
            // Keep a reference to the uploaded PDF so it can be shown side-by-side
            // during approval. The blob URL stays valid even after `file` is cleared.
            setDocumentUrl((prev) => {
                if (prev) URL.revokeObjectURL(prev);
                return file ? URL.createObjectURL(file) : null;
            });
            toast.success('Pdf received');
            handleClear();
        } catch (err) {
            setSdrResult(null);
            console.error('SDR upload failed:', err);
            // The API states the reason in `message` -- a busy document service,
            // a server fault. Show that instead of the generic line, which was
            // all anyone ever saw: the API only sent `error`, and this read
            // `message`, so every real reason was dropped on the floor.
            const message =
                err?.response?.data?.message ||
                'Failed to upload document. Please try again.';
            setError(message);
            toast.error(message);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleClear = () => {
        setFile(null);
        setError('');
        setPageCount(null);
        setEstimatedSeconds(null);
        if (inputRef.current) inputRef.current.value = '';
    };

    const resetSdrFlow = () => {
        setSdrResult(null);
        setExpandedAccordionKey(false);
        setDocumentUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
        });
        handleClear();
    };

    const handleCancel = () => {
        toast.info('Upload cancelled');
        resetSdrFlow();
    };

    // Open the execution-progress modal in read-only mode, showing the results
    // stored for a previous upload (already present in the recent-uploads list).
    const handleViewResults = (row) => {
        let results = row?.execution_results;
        if (typeof results === 'string') {
            try { results = JSON.parse(results); } catch { results = []; }
        }
        setExecutionResults(Array.isArray(results) ? results : []);
        setViewingMeta({ filename: row.filename, document_type: row.document_type });
        setViewingResults(true);
        setIsExecuting(false);
        setShowExecutionDialog(true);
    };

    const closeExecutionDialog = () => {
        setShowExecutionDialog(false);
        if (viewingResults) {
            setViewingResults(false);
            setViewingMeta(null);
            setExecutionResults([]);
        } else {
            resetSdrFlow();
        }
    };

    const handleProceed = async () => {
        if (!sdrResult || !resolvedCompanyId) {
            toast.error('Missing company_id or Ingestion result');
            return;
        }

        console.log('Result JSON passed for DB:', JSON.stringify(sdrResult, null, 2));

        const documentType = String(getUploadDetails(sdrResult)?.document_type || '').toUpperCase().trim();
        // Capture the values to persist now; state may be reset by resetSdrFlow()
        // on success before the recording (in `finally`) runs.
        const recordedPayload = sdrResult;
        const recordedFilename = uploadedFilename;

        setShowExecutionDialog(true);
        setIsExecuting(true);
        setExecutionResults([]);
        setCurrentExecuteStep(0);
        const extractedData = getUploadDetails(sdrResult) || {};
        const localResults = [];

        const pushResult = (item) => {
            localResults.push(item);
            setExecutionResults([...localResults]);
        };

        const buildNeedBody = (customerId) => {
            const customerNeeds = extractedData?.customer_needs || {};
            const employment = extractedData?.employment_and_voting || {};
            const needBody = { customer_id: customerId };
            Object.entries(NEED_GROUPS).forEach(([group, keys]) => {
                keys.forEach((key) => {
                    needBody[key] = resolveNeedText(customerNeeds, group, key).replace(/'/g, "''");
                });
            });
            return {
                ...needBody,
                employment_first_plan: (employment?.employment_status || '').replace(/'/g, "''"),
                employment_plan: (employment?.employment_plan || '').replace(/'/g, "''"),
                voting_plan: (employment?.voting_plan || '').replace(/'/g, "''"),
            };
        };

        let resolvedCustomerId = null;
        let resolvedProjectId = null;
        // Payer ids resolved in the payers step, reused as the project's primary/secondary payer:
        // first MCO entry -> primary, first Private Insurance entry -> secondary (when associable).
        let primaryPayerId = null;
        let secondaryPayerId = null;
        // Contacts written in the contacts step, linked to the project (ProjectContact) once it resolves.
        const projectContactLinks = [];
        // Cross-step links for permitted tasks: goal outcome_number -> customer_goal_id (filled in the goals
        // step) and service authorization -> task-type mapping id (filled in the task-types step).
        const goalIdByOutcome = new Map();
        const serviceAuthTaskLinks = [];

        const customerInfo = extractedData?.customer_information || {};
        const contactDetails = customerInfo?.contact_details || {};
        const dddId = String(customerInfo?.ddd_id || customerInfo?.customer_id || customerInfo?.DDDID || '').trim();
        const customerName = [customerInfo?.first_name, customerInfo?.last_name].filter(Boolean).join(' ').trim();

        const diagnosesList = Array.isArray(extractedData?.diagnosis_information?.diagnoses)
            ? extractedData.diagnosis_information.diagnoses
            : [];
        const findFirstIcdByType = (type) => {
            const match = diagnosesList.find(d => String(d?.diagnosis_type || '').trim().toLowerCase().includes(type));
            return String(match?.icd_code || '').trim();
        };
        const primaryDiagnosisCode = findFirstIcdByType('primary');
        const secondaryDiagnosisCode = findFirstIcdByType('secondary');

        try {
            setCurrentExecuteStep(1);
            const customerAddress = parseAddress(contactDetails?.address || customerInfo?.address || '');
            const hasAddressContent = !!(customerAddress.addressLine1 || customerAddress.addressLine2 || customerAddress.city || customerAddress.state || customerAddress.zip);
            const addressEntries = hasAddressContent ? [{
                address_line_1: customerAddress.addressLine1,
                address_line_2: customerAddress.addressLine2,
                city: customerAddress.city,
                state: customerAddress.state,
                zip: customerAddress.zip,
                latitude: 0,
                longitude: 0,
            }] : [];
            const validPhone = [contactDetails?.home_phone, contactDetails?.cell_phone, contactDetails?.work_phone].find(p => p && p.trim().toUpperCase() !== 'N/A' && p.trim().toUpperCase() !== 'NONE') || '';
            const customerPayload = {
                name: customerName,
                cus_name: customerName,
                first_name: customerInfo?.first_name || '',
                last_name: customerInfo?.last_name || '',
                company_id: resolvedCompanyId,
                comp_id: resolvedCompanyId,
                phone: validPhone,
                phonList: validPhone,
                email: contactDetails?.email || '',
                DDDID: dddId,
                ddd_id: dddId,
                waiver_prgm: customerInfo?.program || '',
                waiver_program: customerInfo?.program || '',
                age: parseInt(String(customerInfo?.age ?? '').replace(/[^0-9]/g, ''), 10) || '',
                dob: toNormalizedDate(customerInfo?.date_of_birth),
                status: 'Active',
                customer_type: 'Individual',
                type: 'Individual',
                is_company: 'false',
                medicaid_id: customerInfo?.medicaid_id || '',
                medicaid_eligible: customerInfo?.medicaid_eligible || '',
                gender: normalizeGenderCode(customerInfo?.gender),
                County: customerInfo?.county || '',
                diagnose_code: primaryDiagnosisCode || customerInfo?.primary_diagnosis_code || customerInfo?.diagnosis_code || '',
                secondary_diagnose_code: secondaryDiagnosisCode || '',
                proId: 'NULL',
                address: addressEntries,
                addList: addressEntries,
            };

            try {
                // Check if customer exists safely by fetching the list and filtering
                const checkResp = await axios.get(`${APIURL}/customers/v2`, {
                    params: { company_id: resolvedCompanyId }
                }).catch(() => null);

                let matchedCust = null;
                if (Array.isArray(checkResp?.data)) {
                    matchedCust = checkResp.data.find(c => String(c.DDDID) === String(dddId));
                }
                let existingCustomerId = matchedCust?.cus_id;

                if (existingCustomerId) {
                    const existingPayload = mapExistingCustomerToPayload(matchedCust);
                    const mergedPayload = mergeSdrOverExisting(existingPayload, customerPayload);
                    const updateResp = await axios.put(`${APIURL}/customers`, { ...mergedPayload, cus_id: existingCustomerId });
                    if (isApiFailure(updateResp.data)) {
                        pushResult({ step: 'customer', status: 'failed', reason: apiFailureReason(updateResp.data, 'Customer update failed') });
                    } else {
                        resolvedCustomerId = existingCustomerId;
                        pushResult({ step: 'customer', status: 'success', action: 'update', id: resolvedCustomerId });
                    }
                } else {
                    const createResp = await axios.post(`${APIURL}/customers/BulkUpload`, customerPayload);
                    const newCustomerId = getIdFromPayload(createResp.data);
                    if (isApiFailure(createResp.data) || !newCustomerId) {
                        pushResult({ step: 'customer', status: 'failed', reason: apiFailureReason(createResp.data, 'Customer insert failed: no id returned') });
                    } else {
                        resolvedCustomerId = newCustomerId;
                        pushResult({ step: 'customer', status: 'success', action: 'insert', id: resolvedCustomerId });
                    }
                }
            } catch (customerErr) {
                pushResult({ step: 'customer', status: 'failed', reason: customerErr?.response?.data?.message || customerErr.message });
            }

            setCurrentExecuteStep(2);
            if (!resolvedCustomerId) {
                pushResult({ step: 'contacts', status: 'skipped', reason: 'Customer id could not be resolved' });
            } else {
                const contactGroups = [
                    { source: Array.isArray(extractedData?.guardianship_contacts) ? extractedData.guardianship_contacts : [], contactType: 'guardian', designationField: 'relationship' },
                    { source: Array.isArray(extractedData?.emergency_contacts) ? extractedData.emergency_contacts : [], contactType: 'emergency', designationField: 'relationship' },
                    { source: Array.isArray(extractedData?.medical_practitioners) ? extractedData.medical_practitioners : [], contactType: 'medical', designationField: 'specialty' },
                    { source: extractedData?.primary_care_physician ? [extractedData.primary_care_physician] : [], contactType: 'physician', designationField: 'notes' },
                    { source: extractedData?.administrative_service_organization ? [extractedData.administrative_service_organization] : [], contactType: 'administrative', designationField: 'id_group' },
                ];

                const existingContactsResp = await axios.get(`${APIURL}/contacts`, { params: { customer_id: resolvedCustomerId, comp_id: resolvedCompanyId } }).catch(() => null);
                const existingContacts = Array.isArray(existingContactsResp?.data) ? existingContactsResp.data : [];

                const isPlaceholder = (s) => {
                    if (!s) return true;
                    const t = String(s).trim().toUpperCase();
                    return t === '' || t === 'N/A' || t === 'NA' || t === 'NONE' || t === 'NULL' || t === 'UNDEFINED';
                };
                const cleanField = (s) => (isPlaceholder(s) ? '' : String(s).trim());
                const cleanDesignation = (s) => {
                    if (isPlaceholder(s)) return '';
                    const parts = String(s).split('/').map((p) => p.trim()).filter((p) => !isPlaceholder(p));
                    return parts.join(' / ');
                };

                const normalize = (v) => String(v || '').trim().toUpperCase();
                const normalizePhone = (v) => String(v || '').replace(/\D/g, '');
                const seenEmails = new Set();
                const seenPhones = new Set();
                const seenNames = new Set();

                let contactAttemptCount = 0;
                for (const group of contactGroups) {
                    for (const item of group.source) {
                        const split = splitFullName(item?.name || item?.cm_name || item?.sc_name || '');
                        const firstName = cleanField(split.firstName);
                        const lastName = cleanField(split.lastName);
                        if (!firstName && !lastName) continue;

                        const parsed = parseAddress(item?.contact_details?.address || '');
                        const contactPayload = {
                            first_name: firstName,
                            last_name: lastName,
                            address_line_1: cleanField(parsed.addressLine1),
                            address_line_2: cleanField(parsed.addressLine2),
                            city: cleanField(parsed.city),
                            state: cleanField(parsed.state),
                            zip: cleanField(parsed.zip),
                            email: cleanField(item?.contact_details?.email || item?.email || item?.sc_email || ''),
                            phoneList: [item?.contact_details?.home_phone, item?.contact_details?.cell_phone, item?.contact_details?.primary_phone, item?.phone_number].find(p => p && !isPlaceholder(p)) || '',
                            designation: cleanDesignation(item?.[group.designationField] || item?.designation || ''),
                            status: 'Active',
                            contact_type: group.contactType,
                            con_note: cleanField(item?.notes),
                            company_id: resolvedCompanyId,
                            customer_id: resolvedCustomerId,
                        };

                        const sdrEmail = contactPayload.email;
                        const sdrPhoneDigits = normalizePhone(contactPayload.phoneList);
                        const sdrNameKey = `${normalize(firstName)}|${normalize(lastName)}`;
                        const sdrEmailKey = sdrEmail ? normalize(sdrEmail) : '';
                        const isDuplicateInBatch = (sdrEmailKey && seenEmails.has(sdrEmailKey))
                            || (sdrPhoneDigits && seenPhones.has(sdrPhoneDigits))
                            || (sdrNameKey !== '|' && seenNames.has(sdrNameKey));
                        if (isDuplicateInBatch) {
                            pushResult({ step: 'contacts', status: 'skipped', reason: 'Duplicate of another contact in this upload', contact_type: group.contactType });
                            continue;
                        }
                        if (sdrEmailKey) seenEmails.add(sdrEmailKey);
                        if (sdrPhoneDigits) seenPhones.add(sdrPhoneDigits);
                        if (sdrNameKey !== '|') seenNames.add(sdrNameKey);

                        const matchesType = (c) => normalize(c.con_contact_type) === normalize(group.contactType);

                        let matchedContact = null;
                        if (sdrEmail) {
                            matchedContact = existingContacts.find(c => matchesType(c) && normalize(c.con_email) === normalize(sdrEmail));
                        }
                        if (!matchedContact && sdrPhoneDigits) {
                            matchedContact = existingContacts.find(c => matchesType(c) && normalizePhone(c.con_phone) === sdrPhoneDigits);
                        }
                        if (!matchedContact) {
                            matchedContact = existingContacts.find(c =>
                                normalize(c.con_first_name) === normalize(firstName) &&
                                normalize(c.con_last_name) === normalize(lastName) &&
                                matchesType(c)
                            );
                        }

                        contactAttemptCount += 1;
                        try {
                            const contactResp = matchedContact && matchedContact.con_id
                                ? await axios.put(`${APIURL}/contacts`, { ...contactPayload, cont_id: matchedContact.con_id, con_id: matchedContact.con_id })
                                : await axios.post(`${APIURL}/contacts`, contactPayload);
                            if (isApiFailure(contactResp.data)) {
                                pushResult({ step: 'contacts', status: 'failed', reason: apiFailureReason(contactResp.data, 'Contact write failed'), contact_type: group.contactType });
                            } else {
                                const linkedContactId = (matchedContact && matchedContact.con_id) || contactResp.data?.id || null;
                                if (linkedContactId) {
                                    projectContactLinks.push({
                                        contId: linkedContactId,
                                        contactType: group.contactType,
                                        relationship: contactPayload.designation || '',
                                    });
                                }
                                pushResult({ step: 'contacts', status: 'success', contact_type: group.contactType });
                            }
                        } catch (contactErr) {
                            pushResult({ step: 'contacts', status: 'failed', reason: contactErr?.response?.data?.message || contactErr.message, contact_type: group.contactType });
                        }
                    }
                }
                if (contactAttemptCount === 0) {
                    pushResult({ step: 'contacts', status: 'skipped', reason: 'No contacts found in extracted data' });
                }
            }

            setCurrentExecuteStep(3);
            try {
                const hospital = extractedData?.preferred_hospital || {};
                const parsedHosp = parseAddress(hospital?.contact_details?.address || '');
                if (!parsedHosp.addressLine1) {
                    pushResult({ step: 'preferred_hospital', status: 'skipped', reason: 'No preferred hospital address found' });
                } else {
                    const hospPhone = [hospital?.contact_details?.phone_number].find(p => p && p.trim().toUpperCase() !== 'N/A' && p.trim().toUpperCase() !== 'NONE') || '';
                    const locPayload = {
                        address_line_1: parsedHosp.addressLine1,
                        address_line_2: parsedHosp.addressLine2,
                        city: parsedHosp.city,
                        state: parsedHosp.state,
                        zip: parsedHosp.zip,
                        latitude: 0,
                        longitude: 0,
                        location_type: 'Hospital',
                        location_status: 'Active',
                        country: 'United States',
                        association_date: '',
                        phone: hospPhone,
                        phonList: hospPhone,
                        company_id: resolvedCompanyId,
                    };

                    const locCheck = await axios.get(`${APIURL}/location`, { params: { comp_id: resolvedCompanyId } }).catch(() => null);
                    let matchedLoc = null;
                    if (Array.isArray(locCheck?.data)) {
                        matchedLoc = locCheck.data.find(l =>
                            String(l.loc_company_id) === String(resolvedCompanyId) &&
                            l.loc_addr_line1 === locPayload.address_line_1 &&
                            l.loc_zip === locPayload.zip
                        );
                    }

                    if (matchedLoc && matchedLoc.loc_id) {
                        pushResult({ step: 'preferred_hospital', status: 'success', action: 'skipped', reason: 'Location already exists' });
                    } else {
                        const locResp = await axios.post(`${APIURL}/location`, locPayload);
                        if (isApiFailure(locResp.data)) {
                            pushResult({ step: 'preferred_hospital', status: 'failed', reason: apiFailureReason(locResp.data, 'Location insert failed') });
                        } else {
                            pushResult({ step: 'preferred_hospital', status: 'success' });
                        }
                    }
                }
            } catch (hospitalErr) {
                pushResult({ step: 'preferred_hospital', status: 'failed', reason: hospitalErr?.response?.data?.message || hospitalErr.message });
            }

            setCurrentExecuteStep(4);
            // Mirror the "Add New Payer" modal (Settings -> Billing, PayerInformationv2.js):
            // associate an existing, claims-supported payer with the company via
            // POST /associate_payer (writes companyPayerRel, which is what surfaces the
            // payer in the Billing -> Payers list). We never create arbitrary payers here.
            // The document only gives a name, so we match it against the same candidate set
            // the modal's dropdown uses (GET /payer filtered to prof_claims_supp yes/enrollment,
            // not already associated) and associate on a confident name match, else skip.
            const payerSources = [
                { entity: 'managed_care_organization', payload: extractedData?.managed_care_organization || {} },
                { entity: 'private_insurance', payload: extractedData?.private_insurance || {} },
            ];

            if (!user?.emp_id) {
                payerSources.forEach((ps) =>
                    pushResult({ step: 'payers', entity: ps.entity, status: 'skipped', reason: 'Missing user context (emp_id) required by /associate_payer' })
                );
            } else {
                // Candidate payers (same source the modal dropdown reads).
                const associablePayersResp = await axios.get(`${APIURL}/payer`, { params: { comp_id: resolvedCompanyId } }).catch(() => null);
                const associablePayers = Array.isArray(associablePayersResp?.data) ? associablePayersResp.data : [];

                // Payers already linked to this company, so we don't double-associate.
                const associatedPayersResp = await axios.get(`${APIURL}/associate_payer`, { params: { compId: resolvedCompanyId } }).catch(() => null);
                const alreadyAssociatedIds = new Set(
                    (Array.isArray(associatedPayersResp?.data) ? associatedPayersResp.data : [])
                        .map((p) => p?.payerId)
                        .filter((id) => id !== undefined && id !== null)
                        .map((id) => String(id))
                );

                // Lowercase + strip non-alphanumerics so casing/punctuation/spacing don't block a match.
                const normalizePayerName = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

                const pad2 = (n) => String(n).padStart(2, '0');
                const formatRelDate = (d) => `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}/${d.getFullYear()}`;
                const relStartDate = formatRelDate(new Date());
                const relEndDate = formatRelDate(new Date(new Date().setFullYear(new Date().getFullYear() + 1)));

                // Remember an associable payer for the project's primary/secondary payer fields.
                const captureProjectPayer = (entity, payerId) => {
                    if (entity === 'managed_care_organization') primaryPayerId = payerId;
                    else if (entity === 'private_insurance') secondaryPayerId = payerId;
                };

                for (const payerSource of payerSources) {
                    const payerName = String(payerSource.payload?.name || '').trim();
                    if (!payerName) {
                        pushResult({ step: 'payers', entity: payerSource.entity, status: 'skipped', reason: 'No payer name found' });
                        continue;
                    }

                    const targetName = normalizePayerName(payerName);
                    const matchedPayer = associablePayers.find(
                        (p) => normalizePayerName(p?.payer_name) === targetName
                            && (p?.prof_claims_supp === 'yes' || p?.prof_claims_supp === 'enrollment')
                    );
                    if (!matchedPayer || !matchedPayer.payer_id) {
                        pushResult({ step: 'payers', entity: payerSource.entity, status: 'skipped', reason: `No matching claims-supported payer found for "${payerName}"` });
                        continue;
                    }

                    if (alreadyAssociatedIds.has(String(matchedPayer.payer_id))) {
                        captureProjectPayer(payerSource.entity, matchedPayer.payer_id);
                        pushResult({ step: 'payers', entity: payerSource.entity, status: 'skipped', reason: 'Payer already associated with company', action: 'skip' });
                        continue;
                    }

                    const payerContactName = splitFullName(payerSource.payload?.cm_name || '');
                    const payerContact = payerSource.payload?.contact_details || {};
                    const associateBody = {
                        payerDets: matchedPayer,
                        compId: resolvedCompanyId,
                        createdBy: user.emp_id,
                        updatedBy: user.emp_id,
                        credDets: {},
                        relStartDate,
                        relEndDate,
                        payerTaxId: payerSource.payload?.payerTaxID || '',
                        payerAddrLine1: payerContact?.address_line_1 || '',
                        payerAddrLine2: payerContact?.address_line_2 || '',
                        payerCity: payerContact?.city || '',
                        payerZip: payerContact?.zip || '',
                        payerPhone: payerContact?.cell_phone || payerContact?.primary_phone || '',
                        conFirstName: payerContactName.firstName || '',
                        conLastName: payerContactName.lastName || '',
                        conDesignation: payerSource.payload?.id_group || '',
                    };

                    try {
                        const payerResp = await axios.post(`${APIURL}/associate_payer`, associateBody);
                        if (isApiFailure(payerResp.data)) {
                            pushResult({ step: 'payers', entity: payerSource.entity, status: 'failed', reason: apiFailureReason(payerResp.data, 'Payer association failed') });
                        } else {
                            alreadyAssociatedIds.add(String(matchedPayer.payer_id));
                            captureProjectPayer(payerSource.entity, matchedPayer.payer_id);
                            pushResult({ step: 'payers', entity: payerSource.entity, status: 'success', action: 'associate' });
                        }
                    } catch (payerErr) {
                        pushResult({ step: 'payers', entity: payerSource.entity, status: 'failed', reason: payerErr?.response?.data?.message || payerErr.message });
                    }
                }
            }

            const projCheckResp = resolvedCustomerId
                ? await axios.get(`${APIURL}/project`, { params: { company_id: resolvedCompanyId } }).catch(() => null)
                : null;
            const matchedProject = matchExistingProject(projCheckResp?.data, resolvedCustomerId, resolvedCompanyId, dddId);

            setCurrentExecuteStep(5);
            const priorAuths = Array.isArray(extractedData?.prior_authorization_and_distribution)
                ? extractedData.prior_authorization_and_distribution
                : [];
            const serviceAuthorizations = Array.isArray(extractedData?.service_authorizations)
                ? extractedData.service_authorizations
                : [];
            const rateChanges = Array.isArray(extractedData?.rate_change) ? extractedData.rate_change : [];
            if (!dddId || priorAuths.length === 0) {
                pushResult({ step: 'service_authorizations', status: 'skipped', reason: 'No DDDID or prior authorization entries found' });
            } else if (!user?.emp_id) {
                pushResult({ step: 'service_authorizations', status: 'failed', reason: 'Missing user context (emp_id) required by /preAuth' });
            } else {
                const toMMDDYYYY = (val) => {
                    if (!val) return '';
                    const ymd = String(val).match(/^(\d{4})-(\d{2})-(\d{2})/);
                    if (ymd) return `${ymd[2]}/${ymd[3]}/${ymd[1]}`;
                    const d = new Date(val);
                    if (Number.isNaN(d.getTime())) return '';
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const dd = String(d.getDate()).padStart(2, '0');
                    return `${mm}/${dd}/${d.getFullYear()}`;
                };
                const pBusinessType = getPrimaryBusinessType(user) ?? SUPPORT_COORDINATION_BUSINESS_TYPE;

                const sanitizeNumeric = (raw) => {
                    if (raw === null || raw === undefined) return '0';
                    const cleaned = String(raw).replace(/[$,]/g, '').trim();
                    if (!cleaned) return '0';
                    const upper = cleaned.toUpperCase();
                    if (upper === 'N/A' || upper === 'NA' || upper === 'NONE' || upper === 'NULL' || upper === 'UNDEFINED') return '0';
                    if (Number.isNaN(Number(cleaned))) return '0';
                    return cleaned;
                };
                const isPlaceholderProc = (s) => {
                    const t = String(s || '').trim().toUpperCase();
                    return t === '' || t === 'N/A' || t === 'NA' || t === 'NONE' || t === 'NULL' || t === 'UNDEFINED';
                };

                // Look up the (possibly rate-change-adjusted) rate for a procedure on a given start date.
                const findRateForProcedure = (procedureCode, rawStartDate) => {
                    if (!procedureCode) return NaN;
                    const svc = serviceAuthorizations.find((s) => s && s.procedure_code === procedureCode);
                    if (!svc) return NaN;
                    let rate = parseFloat(String(svc.rate || '').replace(/[$,\s]/g, ''));
                    if (Number.isNaN(rate)) return NaN;
                    const startDate = rawStartDate ? new Date(rawStartDate) : null;
                    if (startDate && !Number.isNaN(startDate.getTime())) {
                        const applicable = rateChanges
                            .filter((c) => c && c.procedure_code === procedureCode)
                            .find((c) => {
                                const eff = c.new_rate_effective_date ? new Date(c.new_rate_effective_date) : null;
                                return eff && !Number.isNaN(eff.getTime()) && startDate > eff;
                            });
                        if (applicable) {
                            const newRate = parseFloat(String(applicable.new_rate || '').replace(/[$,\s]/g, ''));
                            if (!Number.isNaN(newRate)) rate = newRate;
                        }
                    }
                    return rate;
                };

                // A different major plan version means the document is a new plan rather than a
                // revision of the one already on the project, and the plan's first prior-auth row
                // marks where the new plan starts. Decided only when both versions are known --
                // marking a row that actually continues the current plan would split its
                // cumulative unit tracking downstream.
                const documentPlanMajor = toPlanMajor(extractedData?.plan_id);
                const projectPlanMajor = toPlanMajor(matchedProject?.curr_plan_version);
                const isNewPlan =
                    documentPlanMajor !== null &&
                    projectPlanMajor !== null &&
                    documentPlanMajor !== projectPlanMajor;

                const authArray = [];
                const skippedHeaders = [];
                let minStart = null;
                let maxEnd = null;
                let earliestRowIndex = -1;
                for (const P of priorAuths) {
                    // Fall back to the service authorizations when the PA header
                    // procedure code is blank (matches PriorAuthTable display).
                    const headerProcRaw = resolveProcedureCode(P?.procedure_code, serviceAuthorizations);
                    const headerDddId = String(P?.ddd_id || dddId || '').trim();
                    const headerPriorAuth = String(P?.prior_authorization_number || '').trim();

                    // Require Prior Authorization Number, Procedure Code, and DDD ID
                    // on the header — skip all rows for this entry if any is missing.
                    const missingHeaderFields = [];
                    if (!headerPriorAuth) missingHeaderFields.push('Prior Authorization Number');
                    if (isPlaceholderProc(headerProcRaw)) missingHeaderFields.push('Procedure Code');
                    if (!headerDddId) missingHeaderFields.push('DDD ID');
                    if (missingHeaderFields.length > 0) {
                        skippedHeaders.push(missingHeaderFields.join(', '));
                        continue;
                    }

                    const parsedProcedure = parseProcedureCode(headerProcRaw);
                    const procField = `${parsedProcedure.procedureCode || ''}${parsedProcedure.procedureCodeModifier || ''}`;

                    const headerMemberMedicaid = P?.member_medicaid_id || customerInfo?.medicaid_id || '';
                    const headerProviderMedicaid = P?.provider_medicaid_id || '';

                    const distributions = Array.isArray(P?.distribution) ? P.distribution : [];
                    const rowsToProcess = distributions.length > 0
                        ? distributions
                        : [{
                            start_date: P?.pa_start_date,
                            end_date: P?.pa_end_date,
                            authorized_units: P?.approved_units_till_date,
                        }];

                    for (const D of rowsToProcess) {
                        const startDate = toMMDDYYYY(D?.start_date);
                        const endDate = toMMDDYYYY(D?.end_date);
                        if (!startDate || !endDate) continue;

                        const authUnits = sanitizeNumeric(D?.authorized_units);
                        let authAmount = '0';
                        const rate = findRateForProcedure(headerProcRaw, D?.start_date);
                        const unitsNum = parseFloat(authUnits);
                        if (!Number.isNaN(rate) && !Number.isNaN(unitsNum)) {
                            authAmount = (unitsNum * rate).toFixed(2);
                        }

                        const startMs = new Date(startDate).getTime();
                        const endMs = new Date(endDate).getTime();
                        if (minStart === null || startMs < minStart) {
                            minStart = startMs;
                            earliestRowIndex = authArray.length;
                        }
                        if (maxEnd === null || endMs > maxEnd) maxEnd = endMs;

                        authArray.push([
                            headerDddId,                        // 0  DDDID
                            headerMemberMedicaid,               // 1  memberMedicaid
                            headerProviderMedicaid,             // 2  providerMedicaid
                            headerPriorAuth,                    // 3  priorAuth
                            procField,                          // 4  procedureCode + modifier
                            startDate,                          // 5  startDate (MM/DD/YYYY)
                            endDate,                            // 6  endDate   (MM/DD/YYYY)
                            authUnits,                          // 7  authUnits
                            authAmount,                         // 8  authAmount
                            '',                                 // 9  errCode
                            '',                                 // 10 errDescription
                            '',                                 // 11 isNewPlan
                            '',                                 // 12 serviceType
                        ]);
                    }
                }

                if (authArray.length === 0) {
                    const reason = skippedHeaders.length > 0
                        ? `Prior authorization(s) missing required header field(s): ${skippedHeaders.join('; ')}`
                        : 'No prior authorization rows with valid dates';
                    pushResult({ step: 'service_authorizations', status: 'skipped', reason });
                } else {
                    if (isNewPlan && earliestRowIndex >= 0) {
                        authArray[earliestRowIndex][11] = 'Yes';
                    }
                    try {
                        const payload = {
                            data: authArray,
                            empCompanyId: resolvedCompanyId,
                            empId: user.emp_id,
                            companyName: user.company_name || '',
                            empName: user.emp_name || '',
                            minStartDate: toMMDDYYYY(new Date(minStart).toISOString().slice(0, 10)),
                            maxEndDate: toMMDDYYYY(new Date(maxEnd).toISOString().slice(0, 10)),
                            pBusinessType,
                        };
                        const authResp = await axios.post(`${APIURL}/preAuth`, payload);
                        if (isApiFailure(authResp.data)) {
                            pushResult({ step: 'service_authorizations', status: 'failed', reason: apiFailureReason(authResp.data, 'Prior auth bulk upsert failed') });
                        } else {
                            pushResult({
                                step: 'service_authorizations',
                                status: 'success',
                                count: authArray.length,
                                ...(isNewPlan && earliestRowIndex >= 0
                                    ? { new_plan: `Yes (plan ${projectPlanMajor} -> ${documentPlanMajor}, from ${authArray[earliestRowIndex][5]})` }
                                    : {}),
                            });
                        }
                    } catch (preAuthErr) {
                        pushResult({ step: 'service_authorizations', status: 'failed', reason: preAuthErr?.response?.data?.message || preAuthErr.message });
                    }
                }
            }

            setCurrentExecuteStep(6);
            if (resolvedCustomerId) {
                try {
                    // Project end date = latest end_date across the document's service authorizations
                    // (falls back to one year out when none have a valid end date).
                    const serviceAuthEndMsList = (Array.isArray(extractedData?.service_authorizations) ? extractedData.service_authorizations : [])
                        .map((s) => new Date(s?.end_date).getTime())
                        .filter((t) => !Number.isNaN(t));
                    const projectEndDate = serviceAuthEndMsList.length > 0
                        ? toNormalizedDate(new Date(Math.max(...serviceAuthEndMsList)))
                        : toNormalizedDate(new Date(new Date().setFullYear(new Date().getFullYear() + 1)));
                    const projectPayload = {
                        ddd_id: dddId,
                        active_plan_version: '0',
                        curr_plan_version: toPlanVersion(extractedData?.plan_id),
                        pTitle: `${customerName || 'New Customer'} Support Plan`,
                        pDesc: 'Auto-created by Onboarding & Ingestion (AI Agent)',
                        pCustomer: resolvedCustomerId,
                        pSponsor: null,
                        pContact: null,
                        pManager: null,
                        pstrtDate: toNormalizedDate(customerInfo?.waiver_enrollment_date) || toNormalizedDate(new Date()),
                        pEndDate: projectEndDate,
                        pPrice: 0,
                        pCurrency: 'USD',
                        company_id: resolvedCompanyId,
                        pro_primary_payer: primaryPayerId != null ? parseInt(primaryPayerId, 10) : null,
                        pro_secondary_payer: secondaryPayerId != null ? parseInt(secondaryPayerId, 10) : null,
                        selectedEmp: [],
                        pLoc: [],
                    };

                    if (matchedProject && matchedProject.pro_id) {
                        const existingProjectPayload = mapExistingProjectToPayload(matchedProject);
                        const mergedProjectPayload = mergeSdrOverExisting(existingProjectPayload, projectPayload);
                        const projUpdResp = await axios.put(`${APIURL}/project`, {
                            ...mergedProjectPayload,
                            projId: matchedProject.pro_id,
                            pro_id: matchedProject.pro_id,
                            user_id: user?.emp_id || mergedProjectPayload.user_id || null,
                        });
                        if (isApiFailure(projUpdResp.data)) {
                            pushResult({ step: 'project_lookup', status: 'failed', reason: apiFailureReason(projUpdResp.data, 'Project update failed') });
                        } else {
                            resolvedProjectId = matchedProject.pro_id;
                            pushResult({ step: 'project_lookup', status: 'success', action: 'update', id: resolvedProjectId });
                        }
                    } else {
                        const projectCreateResp = await axios.post(`${APIURL}/project`, projectPayload);
                        const newProjectId = getIdFromPayload(projectCreateResp.data) || projectCreateResp.data?.id || null;
                        if (isApiFailure(projectCreateResp.data) || !newProjectId) {
                            pushResult({ step: 'project_lookup', status: 'failed', reason: apiFailureReason(projectCreateResp.data, 'Project insert failed: no id returned') });
                        } else {
                            resolvedProjectId = newProjectId;
                            pushResult({ step: 'project_lookup', status: 'success', action: 'insert', id: resolvedProjectId });
                        }
                    }
                } catch (projectCreateErr) {
                    pushResult({ step: 'project_lookup', status: 'failed', reason: projectCreateErr?.response?.data?.message || projectCreateErr.message });
                }
            } else {
                pushResult({ step: 'project_lookup', status: 'skipped', reason: 'Customer id unavailable' });
            }

            // Link the contacts written above to the resolved project (ProjectContact).
            if (!resolvedProjectId) {
                if (projectContactLinks.length > 0) {
                    pushResult({ step: 'contacts', status: 'skipped', action: 'link_to_project', reason: 'Project id unavailable for linking contacts' });
                }
            } else if (projectContactLinks.length > 0) {
                const existingProjContactsResp = await axios.get(`${APIURL}/projcontact`, { params: { comp_id: resolvedCompanyId, project_id: resolvedProjectId } }).catch(() => null);
                const linkedContactIds = new Set(
                    (Array.isArray(existingProjContactsResp?.data) ? existingProjContactsResp.data : [])
                        .map((pc) => pc?.cont_id ?? pc?.con_id)
                        .filter((id) => id !== undefined && id !== null)
                        .map((id) => String(id))
                );
                for (const link of projectContactLinks) {
                    if (linkedContactIds.has(String(link.contId))) continue;
                    try {
                        const linkResp = await axios.post(`${APIURL}/projcontact`, {
                            projId: resolvedProjectId,
                            contId: link.contId,
                            pc_relationship: link.relationship ? String(link.relationship).slice(0, 50) : undefined,
                        });
                        if (isApiFailure(linkResp.data)) {
                            pushResult({ step: 'contacts', status: 'failed', action: 'link_to_project', contact_type: link.contactType, reason: apiFailureReason(linkResp.data, 'Project-contact link failed') });
                        } else {
                            linkedContactIds.add(String(link.contId));
                            pushResult({ step: 'contacts', status: 'success', action: 'link_to_project', contact_type: link.contactType });
                        }
                    } catch (linkErr) {
                        pushResult({ step: 'contacts', status: 'failed', action: 'link_to_project', contact_type: link.contactType, reason: linkErr?.response?.data?.message || linkErr.message });
                    }
                }
            }

            setCurrentExecuteStep(7);
            const goals = Array.isArray(extractedData?.customer_goals) ? extractedData.customer_goals : [];
            if (!resolvedCustomerId || !resolvedProjectId || goals.length === 0) {
                pushResult({ step: 'customer_goals', status: 'skipped', reason: 'Missing customer/project or no goals' });
            } else {
                const existingGoalsResp = await axios.get(`${APIURL}/customer_goal`, { params: { project_id: resolvedProjectId } }).catch(() => null);
                const existingGoals = Array.isArray(existingGoalsResp?.data) ? existingGoalsResp.data : [];

                let goalAttemptCount = 0;
                for (const goal of goals) {
                    const detail = String(goal?.outcome_description || '').trim();
                    if (!detail) continue;
                    const goalBody = {
                        customer_id: resolvedCustomerId,
                        project_id: resolvedProjectId,
                        goal_title: toGoalTitle(detail),
                        goal_detail: detail,
                        goal_status: 1,
                        goal_progress: 0,
                    };

                    const normalizeGoal = (v) => String(v || '').trim().toUpperCase();
                    const projectGoals = existingGoals.filter(g =>
                        String(g.customer_id) === String(resolvedCustomerId) &&
                        String(g.project_id) === String(resolvedProjectId)
                    );
                    // Goals ingested before titles carried the outcome description are still
                    // named "Outcome <N>". Match those as a fallback so re-ingesting a plan
                    // renames them in place instead of leaving a duplicate pair behind.
                    const legacyTitle = goal?.outcome_number ? `Outcome ${goal.outcome_number}` : null;
                    const matchedGoal =
                        projectGoals.find(g => normalizeGoal(g.goal_title) === normalizeGoal(goalBody.goal_title)) ||
                        (legacyTitle
                            ? projectGoals.find(g => normalizeGoal(g.goal_title) === normalizeGoal(legacyTitle))
                            : undefined);

                    goalAttemptCount += 1;
                    try {
                        const goalResp = matchedGoal && matchedGoal.customer_goal_id
                            ? await axios.put(`${APIURL}/customer_goal`, { ...goalBody, customer_goal_id: matchedGoal.customer_goal_id })
                            : await axios.post(`${APIURL}/customer_goal`, goalBody);
                        if (isApiFailure(goalResp.data)) {
                            pushResult({ step: 'customer_goals', status: 'failed', reason: apiFailureReason(goalResp.data, 'Goal write failed'), goal: goalBody.goal_title });
                        } else {
                            const resolvedGoalId = (matchedGoal && matchedGoal.customer_goal_id) || goalResp.data?.id || null;
                            const outcomeKey = String(goal?.outcome_number ?? '').trim();
                            if (resolvedGoalId && outcomeKey) goalIdByOutcome.set(outcomeKey, resolvedGoalId);
                            pushResult({ step: 'customer_goals', status: 'success', goal: goalBody.goal_title });
                        }
                    } catch (goalErr) {
                        pushResult({ step: 'customer_goals', status: 'failed', reason: goalErr?.response?.data?.message || goalErr.message, goal: goalBody.goal_title });
                    }
                }
                if (goalAttemptCount === 0) {
                    pushResult({ step: 'customer_goals', status: 'skipped', reason: 'No goals with outcome description found' });
                }
            }

            const needBody = buildNeedBody(resolvedCustomerId);
            const hasAnyFieldData = (body, fields) => fields.some((f) => String(body?.[f] || '').trim() !== '');

            setCurrentExecuteStep(8);
            if (!resolvedCustomerId) {
                pushResult({ step: 'health_needs', status: 'skipped', reason: 'Customer id unavailable' });
            } else {
                try {
                    const existingNeeds = await axios.get(`${APIURL}/customer_health_need/${resolvedCustomerId}`).catch(() => null);
                    const existingId = existingNeeds?.data && existingNeeds.data.length > 0 ? existingNeeds.data[0].id : null;
                    const hasData = hasAnyFieldData(needBody, ['allergies', 'dietary', 'health_hazards', 'self_care']);
                    if (!existingId && !hasData) {
                        pushResult({ step: 'health_needs', status: 'skipped', reason: 'No health needs in document and no existing row' });
                    } else {
                        const healthResp = existingId
                            ? await axios.put(`${APIURL}/customer_health_need/${resolvedCustomerId}`, { ...needBody, id: existingId })
                            : await axios.post(`${APIURL}/customer_health_need`, needBody);
                        if (isApiFailure(healthResp.data)) {
                            pushResult({ step: 'health_needs', status: 'failed', reason: apiFailureReason(healthResp.data, 'Health needs write failed') });
                        } else {
                            pushResult({ step: 'health_needs', status: 'success' });
                        }
                    }
                } catch (healthErr) {
                    pushResult({ step: 'health_needs', status: 'failed', reason: healthErr?.response?.data?.message || healthErr.message });
                }
            }

            setCurrentExecuteStep(9);
            if (!resolvedCustomerId) {
                pushResult({ step: 'support_needs', status: 'skipped', reason: 'Customer id unavailable' });
            } else {
                try {
                    const existingNeeds = await axios.get(`${APIURL}/customer_support_need/${resolvedCustomerId}`).catch(() => null);
                    const existingId = existingNeeds?.data && existingNeeds.data.length > 0 ? existingNeeds.data[0].id : null;
                    const hasData = hasAnyFieldData(needBody, ['behavior', 'mobility', 'religious_cultural', 'support_settings']);
                    if (!existingId && !hasData) {
                        pushResult({ step: 'support_needs', status: 'skipped', reason: 'No support needs in document and no existing row' });
                    } else {
                        const supResp = existingId
                            ? await axios.put(`${APIURL}/customer_support_need/${resolvedCustomerId}`, { ...needBody, id: existingId })
                            : await axios.post(`${APIURL}/customer_support_need`, needBody);
                        if (isApiFailure(supResp.data)) {
                            pushResult({ step: 'support_needs', status: 'failed', reason: apiFailureReason(supResp.data, 'Support needs write failed') });
                        } else {
                            pushResult({ step: 'support_needs', status: 'success' });
                        }
                    }
                } catch (supportErr) {
                    pushResult({ step: 'support_needs', status: 'failed', reason: supportErr?.response?.data?.message || supportErr.message });
                }
            }

            setCurrentExecuteStep(10);
            if (!resolvedCustomerId) {
                pushResult({ step: 'employment_needs', status: 'skipped', reason: 'Customer id unavailable' });
            } else {
                try {
                    const existingNeeds = await axios.get(`${APIURL}/customer_empl_need/${resolvedCustomerId}`).catch(() => null);
                    const existingId = existingNeeds?.data && existingNeeds.data.length > 0 ? existingNeeds.data[0].id : null;
                    const hasData = hasAnyFieldData(needBody, ['employment_first_plan', 'employment_plan', 'voting_plan']);
                    if (!existingId && !hasData) {
                        pushResult({ step: 'employment_needs', status: 'skipped', reason: 'No employment needs in document and no existing row' });
                    } else {
                        const emplResp = existingId
                            ? await axios.put(`${APIURL}/customer_empl_need/${resolvedCustomerId}`, { ...needBody, id: existingId })
                            : await axios.post(`${APIURL}/customer_empl_need`, needBody);
                        if (isApiFailure(emplResp.data)) {
                            pushResult({ step: 'employment_needs', status: 'failed', reason: apiFailureReason(emplResp.data, 'Employment needs write failed') });
                        } else {
                            pushResult({ step: 'employment_needs', status: 'success' });
                        }
                    }
                } catch (employmentErr) {
                    pushResult({ step: 'employment_needs', status: 'failed', reason: employmentErr?.response?.data?.message || employmentErr.message });
                }
            }

            setCurrentExecuteStep(11);
            const medications = Array.isArray(extractedData?.medication_info) ? extractedData.medication_info : [];
            if (!resolvedCustomerId) {
                pushResult({ step: 'medications', status: 'skipped', reason: 'Customer id unavailable' });
            } else if (medications.length === 0) {
                pushResult({ step: 'medications', status: 'skipped', reason: 'No medications found' });
            } else {
                const existingMedsResp = await axios.get(`${APIURL}/customer_medication/by_customer/${resolvedCustomerId}`).catch(() => null);
                const existingMeds = Array.isArray(existingMedsResp?.data) ? existingMedsResp.data : [];

                for (const med of medications) {
                    const rawName = med?.medication_name || '';
                    const searchTerm = parseMedicationSearchTerm(rawName);
                    if (!searchTerm) {
                        pushResult({ step: 'medications', status: 'skipped', medication: rawName, reason: 'Empty medication name' });
                        continue;
                    }
                    try {
                        const suggestResp = await axios.get(`${APIURL}/customer_medication/suggestions`, { params: { searchTerm } });
                        const suggestions = Array.isArray(suggestResp?.data) ? suggestResp.data : [];
                        if (suggestions.length === 0) {
                            pushResult({ step: 'medications', status: 'failed', medication: searchTerm, reason: 'No matching drug found in drug_products' });
                            continue;
                        }
                        const drugID = suggestions[0]?.id;
                        const existing = existingMeds.find((em) => Number(em?.drugID) === Number(drugID));
                        const medBody = {
                            drugID,
                            dosage: med?.dosage_info || '',
                            notes: med?.medication_notes || '',
                        };

                        if (existing?.customer_medication_id) {
                            const updateResp = await axios.put(`${APIURL}/customer_medication/${existing.customer_medication_id}`, medBody);
                            if (isApiFailure(updateResp.data)) {
                                pushResult({ step: 'medications', status: 'failed', medication: searchTerm, reason: apiFailureReason(updateResp.data, 'Medication update failed') });
                            } else {
                                pushResult({ step: 'medications', status: 'success', action: 'update', medication: searchTerm });
                            }
                        } else {
                            const createMedResp = await axios.post(`${APIURL}/customer_medication`, {
                                ...medBody,
                                customer_id: resolvedCustomerId,
                                addedBy: user?.emp_id || null,
                            });
                            if (isApiFailure(createMedResp.data) || !createMedResp?.data?.id) {
                                pushResult({ step: 'medications', status: 'failed', medication: searchTerm, reason: apiFailureReason(createMedResp.data, 'Medication insert failed') });
                            } else {
                                pushResult({ step: 'medications', status: 'success', action: 'insert', medication: searchTerm });
                                existingMeds.push({ customer_medication_id: createMedResp.data.id, drugID });
                            }
                        }
                    } catch (medErr) {
                        pushResult({ step: 'medications', status: 'failed', medication: searchTerm, reason: medErr?.response?.data?.message || medErr.message });
                    }
                }
            }

            setCurrentExecuteStep(12);
            // For each service authorization, ensure its procedure code is linked to a task type for the
            // company (Settings -> Advanced -> Task Types). When the code isn't mapped to any task type yet,
            // create a new task type named after the service and link the procedure code to it. The resolved
            // task-type mapping id (taskProcCode_id) is remembered per service auth for the permitted-task step.
            const serviceAuths = Array.isArray(extractedData?.service_authorizations) ? extractedData.service_authorizations : [];
            if (serviceAuths.length === 0) {
                pushResult({ step: 'task_types', status: 'skipped', reason: 'No service authorizations found' });
            } else {
                // Every procedure code joined with this company's task-type mapping. ddID/taskProcCode_id
                // are null when the procedure code is not linked to a task type for the company.
                const procMapResp = await axios.get(`${APIURL}/procedureCodes/handleMappingForTaskProcCode`, { params: { comp_id: resolvedCompanyId } }).catch(() => null);
                const procMapRows = Array.isArray(procMapResp?.data) ? procMapResp.data : [];

                // Every task type already defined for the company (is_Active != 2), so a service whose
                // task type exists is linked to it instead of inserting a second dropdown with the same
                // name -- duplicate names break the queries that join dropdowns on EntityValue.
                const taskTypeResp = await axios
                    .get(`${APIURL}/dropdowns/dropdownType/taskTypeList`, { params: { comp_id: resolvedCompanyId } })
                    .catch(() => null);
                const taskTypeRows = Array.isArray(taskTypeResp?.data) ? taskTypeResp.data : [];

                const normCode = (s) => String(s || '').trim().toUpperCase();
                const normName = (s) => String(s || '').trim().toUpperCase();

                // Task type name -> ddID for this company.
                const taskTypeIdByName = new Map(
                    taskTypeRows
                        .filter((r) => r?.ddID != null && String(r?.EntityValue || '').trim())
                        .map((r) => [normName(r.EntityValue), r.ddID])
                );

                // Base procedure code (first 5 chars, modifier excluded) -> taskProcCode_id already mapped
                // for this company. Prior auths store the base code and the modifier in separate columns and
                // every downstream match joins on the base code alone, so H2021 and H2021 HI are the same
                // service here; keying this by procCode_id would create a second task type for the modifier
                // variant of a code that is already mapped.
                const taskProcCodeIdByBaseCode = new Map();
                for (const r of procMapRows) {
                    if (r.taskProcCode_id == null) continue;
                    const baseKey = normCode(r.procProcCode);
                    if (baseKey && !taskProcCodeIdByBaseCode.has(baseKey)) {
                        taskProcCodeIdByBaseCode.set(baseKey, r.taskProcCode_id);
                    }
                }

                for (const svc of serviceAuths) {
                    const parsed = parseProcedureCode(svc?.procedure_code);
                    const codeKey = normCode(parsed.procedureCode);
                    const modKey = normCode(parsed.procedureCodeModifier);
                    if (!codeKey) {
                        pushResult({ step: 'task_types', status: 'skipped', reason: 'Service authorization has no procedure code' });
                        continue;
                    }

                    // Resolve the procedure code string to a procedure_code record (prefer a modifier match).
                    const candidates = procMapRows.filter((r) => normCode(r.procProcCode) === codeKey);
                    const matchedRow = candidates.find((r) => normCode(r.procModifierCode) === modKey) || candidates[0];
                    if (!matchedRow) {
                        pushResult({ step: 'task_types', status: 'skipped', reason: `Procedure code "${parsed.procedureCode}" not found in procedure code list` });
                        continue;
                    }

                    let taskProcCodeId = taskProcCodeIdByBaseCode.get(codeKey);

                    if (taskProcCodeId != null) {
                        // Already linked to a task type for this company (or linked earlier in this run).
                        pushResult({ step: 'task_types', status: 'skipped', action: 'skip', reason: `Procedure code "${parsed.procedureCode}" already linked to a task type` });
                    } else {
                        const serviceName = String(svc?.service_name || '').trim();
                        if (!serviceName) {
                            pushResult({ step: 'task_types', status: 'skipped', reason: `No service name to name a task type for procedure code "${parsed.procedureCode}"` });
                            continue;
                        }
                        try {
                            // Reuse the company's task type of the same name when it already exists; only
                            // create one when the name is new.
                            let resolvedTaskTypeId = taskTypeIdByName.get(normName(serviceName));
                            const reusedTaskType = resolvedTaskTypeId != null;

                            if (!reusedTaskType) {
                                // Create a custom/new task type (is_Active=4, matching the Settings UI). comp_id and
                                // is_Active are query params; the entity is in the body.
                                const createTtResp = await axios.post(
                                    `${APIURL}/dropdowns?comp_id=${resolvedCompanyId}&is_Active=4`,
                                    { entityValue: serviceName, entityName: 'taskType' }
                                );
                                const newTaskTypeId = createTtResp.data?.dd_data?.id;
                                if (isApiFailure(createTtResp.data) || !newTaskTypeId) {
                                    pushResult({ step: 'task_types', status: 'failed', task_type: serviceName, reason: apiFailureReason(createTtResp.data, 'Task type creation failed') });
                                    continue;
                                }
                                resolvedTaskTypeId = newTaskTypeId;
                                taskTypeIdByName.set(normName(serviceName), resolvedTaskTypeId);
                            }

                            // Link the procedure code to the task type; the mapping id is the taskProcCode_id.
                            const mapResp = await axios.post(`${APIURL}/procedureCodes/handleMappingForTaskProcCode`, {
                                dropdown_id: resolvedTaskTypeId,
                                procedure_code_id: matchedRow.procCode_id,
                                comp_id: resolvedCompanyId,
                            });
                            if (isApiFailure(mapResp.data) || !mapResp.data?.id) {
                                pushResult({ step: 'task_types', status: 'failed', task_type: serviceName, reason: apiFailureReason(mapResp.data, 'Procedure code mapping failed') });
                                continue;
                            }
                            taskProcCodeId = mapResp.data.id;
                            taskProcCodeIdByBaseCode.set(codeKey, taskProcCodeId);
                            pushResult({
                                step: 'task_types',
                                status: 'success',
                                action: reusedTaskType ? 'link' : 'create_and_link',
                                task_type: serviceName,
                            });
                        } catch (ttErr) {
                            pushResult({ step: 'task_types', status: 'failed', task_type: String(svc?.service_name || '').trim(), reason: ttErr?.response?.data?.message || ttErr.message });
                            continue;
                        }
                    }

                    // Remember the resolved task-type mapping for the permitted-task step.
                    if (taskProcCodeId != null) {
                        serviceAuthTaskLinks.push({ svc, taskProcCodeId });
                    }
                }
            }

            setCurrentExecuteStep(13);
            // Replicate "Add New Permitted Task" (Clients -> Goals): link each service authorization's task type
            // (its taskProcCode_id) to each of its associated goals via a permitted_task row.
            const totalAssociatedGoals = serviceAuthTaskLinks.reduce(
                (n, l) => n + (Array.isArray(l.svc?.associated_goals) ? l.svc.associated_goals.length : 0),
                0
            );
            if (serviceAuthTaskLinks.length === 0 || totalAssociatedGoals === 0) {
                pushResult({ step: 'permitted_tasks', status: 'skipped', reason: 'No service authorization task types with associated goals' });
            } else {
                const toCost = (raw) => {
                    const n = parseFloat(String(raw ?? '').replace(/[$,\s]/g, ''));
                    return Number.isFinite(n) ? n : 0;
                };
                const createdPermittedTasks = new Set(); // `${goalId}:${taskProcCodeId}` handled this run
                for (const { svc, taskProcCodeId } of serviceAuthTaskLinks) {
                    const associatedGoals = Array.isArray(svc?.associated_goals) ? svc.associated_goals : [];
                    for (const ag of associatedGoals) {
                        const outcomeKey = String(ag?.outcome_number ?? '').trim();
                        const goalId = outcomeKey ? goalIdByOutcome.get(outcomeKey) : null;
                        if (!goalId) {
                            pushResult({ step: 'permitted_tasks', status: 'skipped', reason: `No created goal for outcome "${ag?.outcome_number ?? ''}"` });
                            continue;
                        }
                        const dedupKey = `${goalId}:${taskProcCodeId}`;
                        if (createdPermittedTasks.has(dedupKey)) continue;
                        try {
                            await axios.post(`${APIURL}/customer_goal/permitted_task`, {
                                customer_id: resolvedCustomerId,
                                customer_goal_id: goalId,
                                taskProcCode_id: taskProcCodeId,
                                goal_reference: toGoalReference(svc),
                                start_date: toNormalizedDate(svc?.start_date) || null,
                                end_date: toNormalizedDate(svc?.end_date) || null,
                                total_cost: toCost(svc?.total_cost),
                            });
                            createdPermittedTasks.add(dedupKey);
                            pushResult({ step: 'permitted_tasks', status: 'success', action: 'create' });
                        } catch (ptErr) {
                            const status = ptErr?.response?.status;
                            const msg = ptErr?.response?.data?.message || ptErr.message;
                            if (status === 400 && /already exists/i.test(msg)) {
                                createdPermittedTasks.add(dedupKey);
                                pushResult({ step: 'permitted_tasks', status: 'skipped', action: 'skip', reason: 'Permitted task already exists' });
                            } else {
                                pushResult({ step: 'permitted_tasks', status: 'failed', reason: msg });
                            }
                        }
                    }
                }
            }

            const failed = localResults.filter((item) => item.status === 'failed').length;
            const succeeded = localResults.filter((item) => item.status === 'success').length;

            if (failed === 0) {
                toast.success(`Execution complete: ${succeeded} step(s) succeeded.`);
                resetSdrFlow();
            } else if (succeeded > 0) {
                toast.warning(`Execution partial: ${succeeded} succeeded, ${failed} failed.`);
            } else {
                toast.error('Execution failed for all steps.');
            }
        } catch (err) {
            toast.error(`Execution error: ${err.message}`);
        } finally {
            setIsExecuting(false);
            // Persist the upload together with the per-step execution results so it
            // can be reviewed later from "Recent uploads".
            const failedCount = localResults.filter((item) => item.status === 'failed').length;
            const succeededCount = localResults.filter((item) => item.status === 'success').length;
            try {
                await axios.post(`${APIURL}/sdr/uploads`, {
                    com_id: resolvedCompanyId,
                    uploaded_by: user?.emp_id || null,
                    filename: recordedFilename,
                    document_type: documentType,
                    payload: recordedPayload,
                    status: succeededCount === 0 && failedCount > 0 ? 'failed' : 'success',
                    execution_results: localResults,
                });
                fetchRecentUploads();
            } catch (recErr) {
                console.error('Failed to record service plan upload', recErr);
            }
        }
    };

    const uploadDetails = sdrResult ? getUploadDetails(sdrResult) : null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {showUploader && !sdrResult && (
                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <h4>Onboarding & Ingestion (AI Agent)</h4>
                    <Alert severity="info" variant="outlined" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                        <AlertTitle sx={{ mb: 0.5 }}>Before you upload</AlertTitle>
                        <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.25 } }}>
                            {isSupportCoordinationCompany ? (
                                <li>Only valid <strong>NJ Individualized Service Plan (NJISP)</strong> PDFs are accepted &mdash; Service Detail Reports (SDR) and any other document will be rejected.</li>
                            ) : (
                                <li>Only valid <strong>NJ Individualized Service Plan (NJISP)</strong> or <strong>Service Detail Report (SDR)</strong> PDFs are accepted &mdash; any other document will be rejected.</li>
                            )}
                            <li>Only 1 file can be uploaded at a time.</li>
                            <li>Maximum file size is <strong>{MAX_FILE_SIZE_MB} MB</strong>.</li>
                            <li>
                                {isSupportCoordinationCompany
                                    ? 'The Support Coordination company name in the document must match your logged-in company'
                                    : 'The company name in the document must match your logged-in company'}
                                {user?.company_name ? <> (<strong>{user.company_name}</strong>)</> : ''};
                                otherwise the upload will be rejected.
                            </li>
                        </Box>
                    </Alert>
                    <input
                        ref={inputRef}
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                        id="pdf-upload-input"
                    />
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            gap: 2,
                            alignItems: 'stretch',
                        }}
                    >
                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {isProcessing ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2,
                                        py: 4,
                                    }}
                                >
                                    <Box sx={{ position: 'relative', display: 'inline-flex', alignSelf: 'center' }}>
                                        <CircularProgress
                                            size={120}
                                            thickness={4}
                                            variant={estimatedSeconds ? 'determinate' : 'indeterminate'}
                                            value={
                                                estimatedSeconds
                                                    ? Math.min(99, (elapsedSeconds / estimatedSeconds) * 100)
                                                    : undefined
                                            }
                                        />
                                        {estimatedSeconds ? (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    bottom: 0,
                                                    right: 0,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Typography variant="h6" color="text.secondary">
                                                    {`${Math.round(Math.min(99, (elapsedSeconds / estimatedSeconds) * 100))}%`}
                                                </Typography>
                                            </Box>
                                        ) : null}
                                    </Box>
                                    {(() => {
                                        const currentMessage = pickProcessingMessage(elapsedSeconds, estimatedSeconds);
                                        return (
                                            <Typography
                                                key={currentMessage}
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    textAlign: 'center',
                                                    '@keyframes sdrFadeIn': {
                                                        '0%': { opacity: 0, transform: 'translateY(4px)' },
                                                        '100%': { opacity: 1, transform: 'translateY(0)' },
                                                    },
                                                    '@keyframes sdrPulse': {
                                                        '0%, 100%': { opacity: 1 },
                                                        '50%': { opacity: 0.45 },
                                                    },
                                                    animation: 'sdrFadeIn 0.4s ease-out, sdrPulse 2.75s ease-in-out 0.4s infinite',
                                                }}
                                            >
                                                {currentMessage}
                                            </Typography>
                                        );
                                    })()}
                                    {estimatedSeconds ? (
                                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                                            {elapsedSeconds < estimatedSeconds
                                                ? `~${formatDuration(estimatedSeconds - elapsedSeconds)} remaining · ${formatDuration(elapsedSeconds)} elapsed`
                                                : `Taking longer than expected · ${formatDuration(elapsedSeconds)} elapsed`}
                                        </Typography>
                                    ) : (
                                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                                            {formatDuration(elapsedSeconds)} elapsed
                                        </Typography>
                                    )}
                                </Box>
                            ) : (
                                <>
                                    <Box
                                        onDragOver={handleDragOver}
                                        onDragEnter={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                        onClick={() => inputRef.current?.click()}
                                        sx={{
                                            border: '2px dashed',
                                            borderColor: isDragging ? 'primary.main' : 'divider',
                                            borderRadius: 1,
                                            bgcolor: isDragging ? 'action.hover' : 'transparent',
                                            p: 3,
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            transition: 'border-color 0.15s ease, background-color 0.15s ease',
                                            '&:hover': {
                                                borderColor: 'primary.main',
                                                bgcolor: 'action.hover',
                                            },
                                        }}
                                    >
                                        <CloudUploadIcon color={isDragging ? 'primary' : 'action'} sx={{ fontSize: 32 }} />
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            {isDragging
                                                ? 'Drop PDF here'
                                                : 'Drag and drop a PDF here, or click to choose'}
                                        </Typography>
                                    </Box>

                                    {file && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.5,
                                                p: 1.5,
                                                borderRadius: 1,
                                                bgcolor: 'action.hover',
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <InsertDriveFileIcon color="error" />
                                                <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                                                    {file.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    ({(file.size / 1024).toFixed(1)} KB)
                                                </Typography>
                                                <Button size="small" onClick={handleClear}>
                                                    Remove
                                                </Button>
                                            </Box>
                                            {estimatedSeconds && (
                                                <Typography variant="caption" color="text.secondary" sx={{ pl: 4 }}>
                                                    {pageCount} {pageCount === 1 ? 'page' : 'pages'} · estimated processing ~{formatDuration(estimatedSeconds)}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}

                                    {error && (
                                        <Typography variant="body2" color="error">
                                            {error}
                                        </Typography>
                                    )}

                                    <Button type="submit" variant="contained" disabled={!file} fullWidth>
                                        Ingest Document
                                    </Button>
                                </>
                            )}
                        </Box>

                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                            }}
                        >
                            <Typography variant="subtitle2">Recent uploads</Typography>
                            {recentUploads.length === 0 ? (
                                <Typography variant="caption" color="text.secondary">
                                    Your last 5 uploads will appear here.
                                </Typography>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {recentUploads.map((row) => (
                                        <Box
                                            key={row.service_plan_upload_id}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                p: 1,
                                                borderRadius: 1,
                                                bgcolor: 'action.hover',
                                            }}
                                        >
                                            <InsertDriveFileIcon
                                                color={row.status === 'success' ? 'success' : 'error'}
                                                sx={{ fontSize: 20 }}
                                            />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="body2" noWrap>
                                                    {row.filename || '(unnamed)'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                                    {row.document_type} · {new Date(row.uploaded_on).toLocaleString()}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                                    by {row.uploaded_by_name?.trim() || `Employee #${row.uploaded_by}`}
                                                </Typography>
                                            </Box>
                                            {row.execution_results ? (
                                                <Button
                                                    size="small"
                                                    onClick={() => handleViewResults(row)}
                                                    sx={{ flexShrink: 0 }}
                                                >
                                                    View results
                                                </Button>
                                            ) : null}
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            )}

            {sdrResult && (
                <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 190px)' }}>
                    <Box
                        sx={{
                            mt: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexShrink: 0,
                        }}
                    >
                        <Typography variant="h6" sx={{ flex: 1 }}>
                            Please Confirm details
                        </Typography>
                        <Button variant="outlined" color="inherit" onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={handleProceed}>
                            Proceed
                        </Button>
                    </Box>
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            gap: 2,
                            mt: 1,
                        }}
                    >
                        {/* Left: original uploaded document for reference */}
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: { md: 320 },
                                minHeight: { xs: 360, md: 0 },
                                display: 'flex',
                                flexDirection: 'column',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                overflow: 'hidden',
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}
                                noWrap
                            >
                                {`Original document${uploadedFilename ? ` — ${uploadedFilename}` : ''}`}
                            </Typography>
                            {documentUrl ? (
                                <Box
                                    component="iframe"
                                    title="Original document"
                                    src={documentUrl}
                                    sx={{ flex: 1, width: '100%', border: 0, minHeight: 0 }}
                                />
                            ) : (
                                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                                    <Typography variant="body2" color="text.secondary" align="center">
                                        Original document preview is not available for this upload.
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Right: extracted data for approval */}
                        <Box
                            sx={{
                                flex: 1.3,
                                minWidth: 0,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                p: 1.5,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                            }}
                        >
                            <SdrUploadDetailsAccordions
                                uploadDetails={uploadDetails}
                                expandedKey={expandedAccordionKey}
                                onExpandedKeyChange={setExpandedAccordionKey}
                                onSectionFieldChange={onSectionFieldChange}
                                onArrayItemFieldChange={onArrayItemFieldChange}
                                onPrimitiveChange={onPrimitiveChange}
                            />
                        </Box>
                    </Box>
                </Box>
            )}

            <Dialog open={showExecutionDialog} onClose={() => { if (!isExecuting) closeExecutionDialog(); }} fullWidth maxWidth="sm">
                <DialogTitle>
                    {viewingResults
                        ? `Execution Details${viewingMeta?.filename ? ` — ${viewingMeta.filename}` : ''}`
                        : 'Ingestion Execution Progress'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, py: 1 }}>
                        {isExecuting && (
                            <>
                                <LinearProgress />
                                <Typography variant="body2" color="text.secondary">
                                    Processing step {Math.max(currentExecuteStep, 1)} of {EXECUTION_STEPS.length}
                                </Typography>
                            </>
                        )}

                        {!isExecuting && executionResults.length > 0 && (
                            <Typography variant="body2" color="text.secondary">
                                {viewingResults ? 'Step results for this upload.' : 'Execution finished. Review step results below.'}
                            </Typography>
                        )}

                        {!isExecuting && executionResults.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                No execution details were recorded for this upload.
                            </Typography>
                        )}

                        {Object.entries(
                            executionResults.reduce((acc, item) => {
                                const bucket = acc[item.step] || { items: [], order: acc.__order || [] };
                                if (!acc[item.step]) {
                                    bucket.items = [];
                                    bucket.order = acc.__order || [];
                                    bucket.order.push(item.step);
                                }
                                bucket.items.push(item);
                                acc[item.step] = bucket;
                                acc.__order = bucket.order;
                                return acc;
                            }, { __order: [] })
                        )
                            .filter(([step]) => step !== '__order')
                            .map(([step, group]) => {
                                const items = group.items || [];
                                const successCount = items.filter((item) => item.status === 'success').length;
                                const failedCount = items.filter((item) => item.status === 'failed').length;
                                const skippedCount = items.filter((item) => item.status === 'skipped').length;
                                const statusState = failedCount > 0 ? 'failed' : successCount > 0 ? 'completed' : 'skipped';
                                const statusIcon = failedCount > 0
                                    ? <ErrorIcon color="error" fontSize="small" />
                                    : successCount > 0
                                        ? <CheckCircleIcon color="success" fontSize="small" />
                                        : <SkipNextIcon color="warning" fontSize="small" />;

                                return (
                                    <Box
                                        key={step}
                                        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}
                                    >
                                        {statusIcon}
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {formatLabel(step)} - {statusState}
                                                {' '}
                                                ({items.length} {items.length === 1 ? 'item' : 'items'})
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {successCount} successful {successCount === 1 ? 'item' : 'items'}, {failedCount} failed, {skippedCount} skipped
                                            </Typography>
                                            {items
                                                .filter((item) => item.reason)
                                                .map((item, index) => (
                                                    <Typography key={`${step}-${index}`} variant="caption" color="text.secondary" display="block">
                                                        {item.reason}
                                                    </Typography>
                                                ))}
                                        </Box>
                                    </Box>
                                );
                            })}

                        {!isExecuting && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                <Button variant="outlined" onClick={closeExecutionDialog}>
                                    Close
                                </Button>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default SDRUploadForm;
